import { notifications } from '@mantine/notifications'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ChatHistoryItem, CustomToolItem, DesaDetail, PercakapanDetail, PercakapanItem } from '../types'

export async function apiFetch<T>(path: string, init?: RequestInit & { desaId?: string | null }): Promise<T> {
  const { desaId, ...rest } = init ?? {}
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(rest.headers as Record<string, string>),
  }
  if (desaId) {
    headers['X-Desa-Id'] = desaId
  }
  const res = await fetch(path, {
    credentials: 'include',
    ...rest,
    headers,
  })
  if (!res.ok) {
    if (res.status === 401 && !path.includes('/auth/sign-out') && !path.includes('/auth/force-logout-status')) {
      // Cek apakah 401 ini hasil force-logout (admin transfer/demote kepala desa)
      // atau session expired biasa. Hanya dispatch auth:401 untuk force-logout.
      try {
        const check = await fetch('/api/auth/force-logout-status', { credentials: 'include' })
        const data = await check.json()
        if (data.force_logout) {
          window.dispatchEvent(new CustomEvent('auth:401'))
        }
      } catch {
        // Gagal cek → aman, jangan dispatch
      }
    }
    const err = await res.json().catch(() => ({ message: 'Request failed' }))
    // Lampirkan status + body mentah ke Error secara additive agar caller yang
    // butuh detail (mis. 409 konfirmasi pagu TKT-0091) bisa membacanya; caller
    // lama yang hanya membaca .message tetap jalan tanpa perubahan.
    const error = new Error(err.message || `HTTP ${res.status}`) as ApiError
    error.status = res.status
    error.data = err
    throw error
  }
  return res.json()
}

/** Error dari apiFetch dengan status HTTP + body respons untuk penanganan spesifik. */
export interface ApiError extends Error {
  status?: number
  data?: Record<string, unknown>
}

// Session — Better Auth returns { session: {...}, user: {...} }
export function useSession(opts?: { refetchInterval?: number | false }) {
  return useQuery({
    queryKey: ['session'],
    queryFn: () =>
      apiFetch<{
        session: { id: string; token: string; expiresAt: string }
        user: {
          id: string
          name: string
          email: string
          image?: string
          role: string
          desa_id?: string
          status?: string
          permissions?: string
        }
      }>('/api/auth/get-session'),
    retry: false,
    refetchInterval: opts?.refetchInterval ?? false,
  })
}

// User profile — returns full user data from DB (termasuk status, permissions)
export function useUserProfile() {
  return useQuery({
    queryKey: ['user-profile'],
    queryFn: () =>
      apiFetch<{
        id: string
        name: string
        email: string
        image?: string
        role: string
        status: string
        desa_id?: string
        nomor_wa?: string | null
        permissions: string[]
        rejected_reason?: string
        admin_request?: boolean
        managed_desa_ids?: string[] | null
        is_super_admin?: boolean
        desa?: { id: string; slug: string; nama: string }
      }>('/api/users/me'),
    retry: false,
  })
}

// Admin Stats — aggregate semua desa (global)
export interface AdminDashboard {
  counters: {
    desa: { total: number; aktif: number }
    warga: { total: number; thisMonth: number; lastMonth: number }
    surat: { total: number; thisMonth: number; lastMonth: number }
    laporan: { total: number; thisMonth: number; lastMonth: number }
    handoffAktif: number
  }
  laporanByStatus: Array<{ status: string; count: number }>
  suratByStatus: Array<{ status: string; count: number }>
  dailyActivity: Array<{ date: string; laporan: number; surat: number; warga: number }>
  topDesa: Array<{ nama: string; warga: number; surat: number; laporan: number }>
  rating: { avg: number | null; count: number }
}

export function useAdminDashboard(enabled = true) {
  return useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: () => apiFetch<AdminDashboard>('/api/admin/dashboard'),
    enabled,
  })
}

export function useAdminStats(enabled = true) {
  return useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: () =>
      apiFetch<{
        totalDesa: number
        desaAktif: number
        totalWarga: number
        suratPending: number
        laporanBaru: number
      }>('/api/admin/stats'),
    enabled,
  })
}

// Desa List — all desa dengan summary (global)
export function useDesaList() {
  return useQuery({
    queryKey: ['desa', 'list'],
    queryFn: () =>
      apiFetch<
        Array<{
          id: string
          nama: string
          slug: string
          kecamatan: string | null
          kabupaten: string | null
          provinsi: string | null
          isActive: boolean
          waConnected: boolean
          waProvider: string
          tgConnected: boolean
          wargaCount: number
          suratPending: number
          desa_plus_id: string | null
          desa_plus_enabled: boolean
        }>
      >('/api/desa/all'),
  })
}

// Stats (per-desa — butuh X-Desa-Id)
export function useStats(desaId?: string | null) {
  return useQuery({
    queryKey: ['stats', desaId],
    queryFn: () =>
      apiFetch<{ warga: number; suratPending: number; laporanBaru: number; documents: number }>('/api/stats', {
        desaId,
      }),
    enabled: !!desaId,
  })
}

export interface DashboardStats {
  counters: {
    warga: { total: number; thisMonth: number; lastMonth: number }
    surat: { total: number; thisMonth: number; lastMonth: number }
    laporan: { total: number; thisMonth: number; lastMonth: number }
    handoffAktif: number
  }
  laporanByStatus: Array<{ status: string; count: number }>
  suratByStatus: Array<{ status: string; count: number }>
  laporanByKategori: Array<{ kategori: string; count: number }>
  rating: { avg: number | null; count: number }
  dailyActivity: Array<{ date: string; laporan: number; surat: number; warga: number }>
  latestLaporan: Array<{
    id: string
    kategori: string
    status: string
    priority: string
    created_at: string
    warga: { nama: string }
  }>
  latestSurat: Array<{ id: string; jenis: string; status: string; created_at: string; warga: { nama: string } }>
}

export function useDashboardStats(desaId?: string | null) {
  return useQuery({
    queryKey: ['stats', 'dashboard', desaId],
    queryFn: () => apiFetch<DashboardStats>('/api/stats/dashboard', { desaId }),
    enabled: !!desaId,
  })
}

// Paginated response type
interface Paginated<T> {
  data: T[]
  total: number
  page: number
  limit: number
}

// Surat (per-desa)
export function useSuratSummary(desaId?: string | null) {
  return useQuery({
    queryKey: ['surat', 'summary', desaId],
    queryFn: () => apiFetch<Record<string, number>>('/api/surat/summary', { desaId }),
    enabled: !!desaId,
  })
}

export function useSurat(
  page = 1,
  limit = 20,
  desaId?: string | null,
  filters?: { status?: string; jenis?: string; search?: string },
) {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) })
  if (filters?.status) params.set('status', filters.status)
  if (filters?.jenis) params.set('jenis', filters.jenis)
  if (filters?.search) params.set('search', filters.search)
  return useQuery({
    queryKey: ['surat', desaId, page, limit, filters?.status, filters?.jenis, filters?.search],
    queryFn: () => apiFetch<Paginated<any>>(`/api/surat?${params}`, { desaId }),
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

export function useSuratDetail(id: string | null, desaId?: string | null) {
  return useQuery({
    queryKey: ['surat', id, desaId],
    queryFn: () => apiFetch<any>(`/api/surat/${id}`, { desaId }),
    enabled: !!id && !!desaId,
  })
}

// Panel governance draft surat (tiket db952320 / e339a7cb Fase 3).
// Metadata-only: backend TIDAK mengirim kolom `data` (isi-dokumen PII).
export interface DraftSuratItem {
  id: string
  jenis: string
  nama_warga: string
  created_at: string
  updated_at: string
  expiresInDays: number
}

export function useDraftSurat(desaId?: string | null, page = 1, limit = 20) {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) })
  return useQuery({
    queryKey: ['draft-surat', desaId, page, limit],
    queryFn: () => apiFetch<Paginated<DraftSuratItem>>(`/api/draft-surat?${params}`, { desaId }),
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

export function useDeleteDraftSurat(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/draft-surat/${id}`, { method: 'DELETE', desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['draft-surat'] })
    },
  })
}

export function useCreateSurat(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { template_id: string; warga_id: string; keperluan?: string; data?: Record<string, unknown> }) =>
      apiFetch('/api/surat', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['surat'] })
      qc.invalidateQueries({ queryKey: ['stats'] })
    },
  })
}

export function useProcessSurat(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/surat/${id}/process`, { method: 'PATCH', desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['surat'] })
      qc.invalidateQueries({ queryKey: ['stats'] })
    },
  })
}

export function useApproveSurat(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/surat/${id}/approve`, { method: 'PATCH', desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['surat'] })
      qc.invalidateQueries({ queryKey: ['stats'] })
    },
  })
}

export function useRejectSurat(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, alasan }: { id: string; alasan: string }) =>
      apiFetch(`/api/surat/${id}/reject`, { method: 'PATCH', body: JSON.stringify({ alasan }), desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['surat'] })
      qc.invalidateQueries({ queryKey: ['stats'] })
    },
  })
}

// GAP-4: petugas kembalikan surat ke warga untuk dilengkapi (catatan wajib), bukan tolak final.
export function useReturnSurat(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, catatan }: { id: string; catatan: string }) =>
      apiFetch(`/api/surat/${id}/return`, { method: 'PATCH', body: JSON.stringify({ catatan }), desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['surat'] })
      qc.invalidateQueries({ queryKey: ['stats'] })
    },
  })
}

export function useCompleteSurat(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/surat/${id}/complete`, { method: 'PATCH', desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['surat'] })
      qc.invalidateQueries({ queryKey: ['stats'] })
    },
  })
}

// GAP-1 Model A: tombol staff "Terbitkan Surat" — trigger manual ttd_pejabat → terbit.
export function useTerbitkanSurat(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/surat/${id}/terbitkan`, { method: 'PATCH', desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['surat'] })
      qc.invalidateQueries({ queryKey: ['stats'] })
    },
  })
}

export function useDownloadSuratPDF(desaId?: string | null) {
  return useMutation({
    mutationFn: async (id: string) => {
      const data = await apiFetch<{ url: string }>(`/api/surat/${id}/pdf`, { desaId })
      window.open(data.url, '_blank')
      return data
    },
  })
}

export function useResendSuratPDF(desaId?: string | null) {
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ success: boolean; message: string }>(`/api/surat/${id}/resend-pdf`, { method: 'POST', desaId }),
  })
}

// ─── GAP-3: Rantai Penandatangan (co-signer sekuensial sebelum TTD final) ───

/** Satu langkah rantai TTD (SuratPenandatangan). */
export interface PenandatanganStep {
  id: string
  urutan: number
  jenis: string
  nama_pihak_ketiga: string | null
  jabatan_pihak_ketiga: string | null
  status: string // 'menunggu' | 'selesai' | 'dilewati'
  ttd_url: string | null
  ditandatangani_at: string | null
}

/** Daftar langkah rantai co-signer untuk satu surat (terurut naik). */
export function usePenandatangan(suratId: string | null, desaId?: string | null) {
  return useQuery({
    queryKey: ['penandatangan', suratId, desaId],
    queryFn: () => apiFetch<PenandatanganStep[]>(`/api/surat/${suratId}/penandatangan`, { desaId }),
    enabled: !!suratId && !!desaId,
  })
}

/** Unggah bukti TTD saksi manual → tandai langkah selesai (FormData, bukan JSON). */
export function useCompletePenandatanganStep(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ suratId, stepId, file }: { suratId: string; stepId: string; file: File }) => {
      const fd = new FormData()
      fd.append('file', file)
      const headers: Record<string, string> = {}
      if (desaId) headers['X-Desa-Id'] = desaId
      const res = await fetch(`/api/surat/${suratId}/penandatangan/${stepId}/ttd`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: fd,
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Unggah bukti TTD gagal' }))
        throw new Error(err.message ?? 'Unggah bukti TTD gagal')
      }
      return res.json() as Promise<PenandatanganStep>
    },
    onSuccess: (_data, { suratId }) => {
      qc.invalidateQueries({ queryKey: ['penandatangan', suratId] })
      qc.invalidateQueries({ queryKey: ['surat'] })
    },
  })
}

/** Lewati satu langkah (saksi tak tersedia) — alasan wajib. */
export function useSkipPenandatanganStep(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ suratId, stepId, alasan }: { suratId: string; stepId: string; alasan: string }) =>
      apiFetch<PenandatanganStep>(`/api/surat/${suratId}/penandatangan/${stepId}/skip`, {
        method: 'PATCH',
        desaId,
        body: JSON.stringify({ alasan }),
      }),
    onSuccess: (_data, { suratId }) => {
      qc.invalidateQueries({ queryKey: ['penandatangan', suratId] })
      qc.invalidateQueries({ queryKey: ['surat'] })
    },
  })
}

// ─── GAP-1 Fase 5: Rantai Approval Pengantar Wilayah ───

/** Satu langkah rantai pengantar wilayah (SuratPengantarApproval). */
export interface PengantarStep {
  level: number
  wilayah_nama: string
  jabatan_label: string | null
  pejabat_nama: string | null
  status: string // 'pending' | 'approved' | 'rejected'
  channel: string | null
  approved_at: string | null
  catatan: string | null
}

/** Progres rantai pengantar wilayah untuk satu surat. */
export interface PengantarProgress {
  surat_id: string
  status: string
  model: 'A' | 'B' | null
  total: number
  approved: number
  rejected: number
  complete: boolean
  steps: PengantarStep[]
}

/** Progres rantai pengantar wilayah — null/total=0 berarti surat tak butuh pengantar. */
export function usePengantarProgress(suratId: string | null, desaId?: string | null) {
  return useQuery({
    queryKey: ['pengantar', suratId, desaId],
    queryFn: () => apiFetch<PengantarProgress>(`/api/surat/${suratId}/pengantar`, { desaId }),
    enabled: !!suratId && !!desaId,
  })
}

/** Staff setujui satu langkah pengantar (atas nama pejabat wilayah) — catatan opsional. */
export function useApprovePengantarStep(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ suratId, level, catatan }: { suratId: string; level: number; catatan?: string }) =>
      apiFetch<{ ok: boolean; surat_status: string | null }>(`/api/surat/${suratId}/pengantar/${level}/approve`, {
        method: 'PATCH',
        desaId,
        body: JSON.stringify({ catatan }),
      }),
    onSuccess: (_data, { suratId }) => {
      qc.invalidateQueries({ queryKey: ['pengantar', suratId] })
      qc.invalidateQueries({ queryKey: ['surat'] })
    },
  })
}

/** Staff tolak satu langkah pengantar — alasan wajib. */
export function useRejectPengantarStep(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ suratId, level, alasan }: { suratId: string; level: number; alasan: string }) =>
      apiFetch<{ ok: boolean; surat_status: string | null }>(`/api/surat/${suratId}/pengantar/${level}/reject`, {
        method: 'PATCH',
        desaId,
        body: JSON.stringify({ alasan }),
      }),
    onSuccess: (_data, { suratId }) => {
      qc.invalidateQueries({ queryKey: ['pengantar', suratId] })
      qc.invalidateQueries({ queryKey: ['surat'] })
    },
  })
}

// Laporan (per-desa)
export function useLaporan(
  page = 1,
  limit = 20,
  desaId?: string | null,
  filters?: {
    status?: string
    priority?: string
    kategori?: string
    search?: string
    near_deadline?: boolean
    sortBy?: string
    assigned_to?: string
  },
) {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) })
  if (filters?.status) params.set('status', filters.status)
  if (filters?.priority) params.set('priority', filters.priority)
  if (filters?.kategori) params.set('kategori', filters.kategori)
  if (filters?.search) params.set('search', filters.search)
  if (filters?.near_deadline) params.set('near_deadline', 'true')
  if (filters?.sortBy) params.set('sortBy', filters.sortBy)
  if (filters?.assigned_to) params.set('assigned_to', filters.assigned_to)
  return useQuery({
    queryKey: [
      'laporan',
      desaId,
      page,
      limit,
      filters?.status,
      filters?.priority,
      filters?.kategori,
      filters?.search,
      filters?.near_deadline,
      filters?.sortBy,
      filters?.assigned_to,
    ],
    queryFn: () => apiFetch<Paginated<any>>(`/api/laporan?${params}`, { desaId }),
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

export function useLaporanSummary(desaId?: string | null) {
  return useQuery({
    queryKey: ['laporan', 'summary', desaId],
    queryFn: () => apiFetch<Record<string, number>>('/api/laporan/summary', { desaId }),
    enabled: !!desaId,
  })
}

// Jumlah laporan aktif yang ditugaskan ke user saat ini (badge nav "Tugas Saya")
export function useMyAssignedCount(desaId?: string | null) {
  return useQuery({
    queryKey: ['laporan', 'assigned-count', desaId],
    queryFn: () => apiFetch<{ count: number }>('/api/laporan/assigned-count', { desaId }),
    enabled: !!desaId,
    refetchInterval: 60_000,
  })
}

export function useLaporanDetail(id: string | null, desaId?: string | null) {
  return useQuery({
    queryKey: ['laporan', id, desaId],
    queryFn: () => apiFetch<any>(`/api/laporan/${id}`, { desaId }),
    enabled: !!id && !!desaId,
  })
}

export function useUpdateLaporanStatus(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status, reason }: { id: string; status: string; reason?: string }) =>
      apiFetch(`/api/laporan/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status, reason }), desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['laporan'] })
      qc.invalidateQueries({ queryKey: ['stats'] })
    },
  })
}

export function useAssignLaporan(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, petugas_id }: { id: string; petugas_id: string }) =>
      apiFetch(`/api/laporan/${id}/assign`, { method: 'PATCH', body: JSON.stringify({ petugas_id }), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['laporan'] }),
  })
}

export function useEscalateLaporan(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, petugas_id, reason }: { id: string; petugas_id: string; reason: string }) =>
      apiFetch(`/api/laporan/${id}/escalate`, {
        method: 'PATCH',
        body: JSON.stringify({ petugas_id, reason }),
        desaId,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['laporan'] }),
  })
}

export function useCommentLaporan(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      apiFetch(`/api/laporan/${id}/comment`, { method: 'POST', body: JSON.stringify({ note }), desaId }),
    onSuccess: (_data, vars) => qc.invalidateQueries({ queryKey: ['laporan', vars.id] }),
  })
}

export function useProgressLaporan(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, note, foto }: { id: string; note: string; foto?: File }) => {
      const formData = new FormData()
      formData.append('note', note)
      if (foto) formData.append('foto', foto)
      const headers: Record<string, string> = {}
      if (desaId) headers['X-Desa-Id'] = desaId
      const res = await fetch(`/api/laporan/${id}/progress`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: formData,
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Gagal update progress' }))
        throw new Error(err.message || `HTTP ${res.status}`)
      }
      return res.json()
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['laporan', vars.id] })
      qc.invalidateQueries({ queryKey: ['laporan'] })
    },
  })
}

export function useLaporanMedia(id: string | null, desaId?: string | null) {
  return useQuery({
    queryKey: ['laporan', 'media', id, desaId],
    queryFn: () =>
      apiFetch<{ fotos: string[]; video: string | null; video_mime: string | null; thumbnail: string | null }>(
        `/api/laporan/${id}/media`,
        { desaId },
      ),
    enabled: !!id && !!desaId,
    staleTime: 50 * 60 * 1000, // 50 menit (URL berlaku 1 jam)
  })
}

// Warga (per-desa)
export function useWarga(search = '', desaId?: string | null, page = 1, limit = 20, status?: string | null) {
  return useQuery({
    queryKey: ['warga', desaId, search, page, limit, status],
    queryFn: () => {
      const params = new URLSearchParams({
        search: encodeURIComponent(search),
        page: String(page),
        limit: String(limit),
      })
      if (status) params.set('status', status)
      return apiFetch<
        Paginated<any> & { statusCounts: { aktif: number; belum_verifikasi: number; with_channel: number } }
      >(`/api/warga?${params}`, { desaId })
    },
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

export function useCreateWarga(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      nama: string
      nik: string
      no_hp?: string | null
      tanggal_lahir?: string | null
      alamat?: string | null
      wilayah?: string | null
      wilayah_id?: string | null
      status?: string
    }) => apiFetch('/api/warga', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warga', desaId] })
      qc.invalidateQueries({ queryKey: ['stats'] })
    },
  })
}

export interface ImportPreviewResult {
  valid: Array<{
    row: number
    nik: string
    nama: string
    no_hp: string | null
    tanggal_lahir: string | null
    alamat: string | null
    wilayah: string | null
  }>
  errors: Array<{ row: number; nik: string; nama: string; error: string }>
  totalRows: number
  warnings?: string[]
}

export function useImportWargaPreview(desaId?: string | null) {
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      const headers: Record<string, string> = {}
      if (desaId) headers['X-Desa-Id'] = desaId
      const res = await fetch('/api/warga/import/preview', {
        method: 'POST',
        credentials: 'include',
        headers,
        body: formData,
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Upload gagal' }))
        throw new Error(err.message || `HTTP ${res.status}`)
      }
      return res.json() as Promise<ImportPreviewResult>
    },
  })
}

export function useImportWargaConfirm(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (
      rows: Array<{
        nik: string
        nama: string
        no_hp?: string | null
        tanggal_lahir?: string | null
        alamat?: string | null
        wilayah?: string | null
      }>,
    ) =>
      apiFetch<{ inserted: number; skipped: number; total: number }>('/api/warga/import/confirm', {
        method: 'POST',
        body: JSON.stringify({ rows }),
        desaId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warga', desaId] })
      qc.invalidateQueries({ queryKey: ['stats'] })
    },
  })
}

export function useUpdateWarga(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiFetch(`/api/warga/${id}`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['warga', desaId] }),
  })
}

export function useDeleteWarga(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/warga/${id}`, { method: 'DELETE', desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warga', desaId] })
      qc.invalidateQueries({ queryKey: ['stats'] })
    },
  })
}

// Gate KTP Group D2: detail warga fresh (termasuk ktpDownloadUrl presigned) untuk drawer Detail Warga.
export function useWargaDetail(id: string | null, desaId?: string | null) {
  return useQuery({
    queryKey: ['warga', 'detail', id, desaId],
    queryFn: () => apiFetch<any>(`/api/warga/${id}`, { desaId }),
    enabled: !!id && !!desaId,
  })
}

// Gate KTP Group D3/D4: tandai KTP warga terverifikasi manual oleh petugas.
export function useVerifikasiKtpWarga(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/warga/${id}/verifikasi-ktp`, { method: 'PATCH', desaId }),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['warga', desaId] })
      qc.invalidateQueries({ queryKey: ['warga', 'detail', id, desaId] })
    },
  })
}

// Gate KTP v2 Group K5: tolak foto KTP warga dengan alasan wajib (README K-14).
export function useTolakKtpWarga(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, alasan }: { id: string; alasan: string }) =>
      apiFetch(`/api/warga/${id}/tolak-ktp`, { method: 'PATCH', body: JSON.stringify({ alasan }), desaId }),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: ['warga', desaId] })
      qc.invalidateQueries({ queryKey: ['warga', 'detail', id, desaId] })
    },
  })
}

// Kirim pesan langsung ke warga
export function useSendDirectMessage(desaId?: string | null) {
  return useMutation({
    mutationFn: ({ wargaId, text, channel }: { wargaId: string; text: string; channel?: string }) =>
      apiFetch<{ ok?: boolean; error?: string; channel?: string }>(`/api/warga/${wargaId}/send-message`, {
        method: 'POST',
        body: JSON.stringify({ text, channel }),
        desaId,
      }),
  })
}

// Kirim file langsung ke warga
export function useSendDirectFile(desaId?: string | null) {
  return useMutation({
    mutationFn: async ({
      wargaId,
      file,
      caption,
      channel,
    }: {
      wargaId: string
      file: File
      caption?: string
      channel?: string
    }) => {
      const formData = new FormData()
      formData.append('file', file)
      if (caption) formData.append('caption', caption)
      if (channel) formData.append('channel', channel)

      const headers: Record<string, string> = {}
      if (desaId) headers['X-Desa-Id'] = desaId

      const res = await fetch(`/api/warga/${wargaId}/send-file`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: formData,
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Upload gagal' }))
        throw new Error(err.message || `HTTP ${res.status}`)
      }
      return res.json() as Promise<{ ok?: boolean; error?: string; channel?: string; fileName?: string }>
    },
  })
}

// Staff profile & shift management
export function useUpdateStaffProfile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: { nomor_wa?: string | null; jabatan?: string | null; telegram_chat_id?: string | null }
    }) => apiFetch(`/api/staff-users/${id}/profile`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staff-users'] })
    },
  })
}

export function useToggleStaffShift() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, shift_aktif }: { id: string; shift_aktif: boolean }) =>
      apiFetch(`/api/staff-users/${id}/shift`, { method: 'PATCH', body: JSON.stringify({ shift_aktif }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staff-users'] })
    },
    onError: () => {
      notifications.show({ title: 'Gagal', message: 'Tidak punya wewenang mengubah shift', color: 'red' })
    },
  })
}

// Jabatan options (dropdown data)
export function useJabatanOptions(desaId?: string | null) {
  return useQuery({
    queryKey: ['jabatan-options', desaId],
    queryFn: () => apiFetch<{ options: string[] }>('/api/users/jabatan-options', { desaId }),
  })
}

export function useJabatanTemplate() {
  return useQuery({
    queryKey: ['jabatan-template'],
    queryFn: () => apiFetch<{ template: string[] }>('/api/staff-users/jabatan-template'),
  })
}

export function useAddJabatanOptions(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (items: string[]) =>
      apiFetch('/api/staff-users/jabatan-options', { method: 'POST', body: JSON.stringify({ items, desaId }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jabatan-options'] })
    },
  })
}

export function useSetJabatanOptions(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (options: string[]) =>
      apiFetch('/api/staff-users/jabatan-options', { method: 'PUT', body: JSON.stringify({ options, desaId }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['jabatan-options'] })
    },
  })
}

// Bansos (per-desa)
export function useBansosList(
  desaId?: string | null,
  filters?: { program?: string; status?: string; search?: string },
  page = 1,
  limit = 20,
) {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) })
  if (filters?.program) params.set('program', filters.program)
  if (filters?.status) params.set('status', filters.status)
  if (filters?.search) params.set('search', filters.search)
  return useQuery({
    queryKey: ['bansos', desaId, page, limit, filters?.program, filters?.status, filters?.search],
    queryFn: () => apiFetch<Paginated<any>>(`/api/bansos?${params}`, { desaId }),
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

export function useBansosStats(desaId?: string | null) {
  return useQuery({
    queryKey: ['bansos', 'stats', desaId],
    queryFn: () =>
      apiFetch<{
        programs: Array<{ program: string; aktif: number; dicabut: number; selesai: number; total: number }>
        total: number
      }>('/api/bansos/stats', { desaId }),
    enabled: !!desaId,
  })
}

export function useCreateBansos(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      nik: string
      nama: string
      program: string
      status?: string
      periode?: string
      keterangan?: string
    }) => apiFetch('/api/bansos', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bansos', desaId] })
      qc.invalidateQueries({ queryKey: ['bansos', 'stats', desaId] })
    },
  })
}

export function useImportBansos(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Array<{ nik: string; nama: string; program: string; periode?: string; keterangan?: string }>) =>
      apiFetch<{ imported: number; skipped: number; total: number }>('/api/bansos/import', {
        method: 'POST',
        body: JSON.stringify({ data }),
        desaId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bansos', desaId] })
      qc.invalidateQueries({ queryKey: ['bansos', 'stats', desaId] })
    },
  })
}

export function useUpdateBansos(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { status?: string; keterangan?: string; periode?: string } }) =>
      apiFetch(`/api/bansos/${id}`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bansos', desaId] })
      qc.invalidateQueries({ queryKey: ['bansos', 'stats', desaId] })
    },
  })
}

export function useDeleteBansos(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/bansos/${id}`, { method: 'DELETE', desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bansos', desaId] })
      qc.invalidateQueries({ queryKey: ['bansos', 'stats', desaId] })
    },
  })
}

// Dokumen (per-desa)
export function useDokumen(desaId?: string | null, hasProcessing = false) {
  return useQuery({
    queryKey: ['dokumen', desaId],
    queryFn: () => apiFetch<any[]>('/api/upload/documents', { desaId }),
    enabled: !!desaId,
    refetchInterval: hasProcessing ? 3000 : false, // polling saat ada dokumen processing
  })
}

export function useDokumenChunks(documentId: string | null, desaId?: string | null) {
  return useQuery({
    queryKey: ['dokumen', 'chunks', documentId, desaId],
    queryFn: () =>
      apiFetch<Array<{ id: string; content: string; metadata: any; created_at: string }>>(
        `/api/upload/document/${documentId}/chunks`,
        { desaId },
      ),
    enabled: !!documentId && !!desaId,
  })
}

export function useTestChat(desaId?: string | null) {
  return useMutation({
    mutationFn: (data: { text: string; phone: string }) =>
      apiFetch<{ traceId: string | null; response: string; debug: Record<string, unknown> }>(
        '/api/ai-pipeline/simulate-chat',
        {
          method: 'POST',
          body: JSON.stringify({ text: data.text, phone: data.phone, channel: 'web' }),
          desaId,
        },
      ),
  })
}

export function useResetTestChat(desaId?: string | null) {
  return useMutation({
    mutationFn: (phone: string) =>
      apiFetch<{ ok: boolean }>(
        `/api/ai-pipeline/simulate-chat/session?phone=${encodeURIComponent(phone)}&channel=web`,
        { method: 'DELETE', desaId },
      ),
  })
}

export function useRagSearch(desaId?: string | null) {
  return useMutation({
    mutationFn: (query: string) =>
      apiFetch<{
        results: Array<{ filename: string; chunks: Array<{ content: string; score: number; index: number }> }>
        totalChunks: number
      }>('/api/upload/search', { method: 'POST', body: JSON.stringify({ query }), desaId }),
  })
}

export function useDownloadDokumen(desaId?: string | null) {
  return useMutation({
    mutationFn: async (id: string) => {
      const data = await apiFetch<{ url: string; filename: string }>(`/api/upload/document/${id}/download`, { desaId })
      // Buka presigned URL di tab baru
      window.open(data.url, '_blank')
      return data
    },
  })
}

export function useUploadDokumen(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      const headers: Record<string, string> = {}
      if (desaId) headers['X-Desa-Id'] = desaId
      const res = await fetch('/api/upload/document', {
        method: 'POST',
        credentials: 'include',
        headers,
        body: formData,
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Upload gagal' }))
        throw new Error(err.message || `HTTP ${res.status}`)
      }
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dokumen'] })
      qc.invalidateQueries({ queryKey: ['stats'] })
    },
  })
}

export function useDeleteDokumen(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/upload/document/${id}`, { method: 'DELETE', desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dokumen'] })
      qc.invalidateQueries({ queryKey: ['stats'] })
    },
  })
}

export function useToggleRagDokumen(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ id: string; rag_active: boolean }>(`/api/upload/document/${id}/rag-toggle`, {
        method: 'PATCH',
        desaId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dokumen'] })
    },
  })
}

export function useRetryDokumen(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/upload/document/${id}/retry`, { method: 'POST', desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dokumen'] })
      qc.invalidateQueries({ queryKey: ['stats'] })
    },
  })
}

// Desa — profil desa by ID
export function useDesa(desaId?: string | null) {
  return useQuery({
    queryKey: ['desa', desaId],
    queryFn: () => apiFetch<DesaDetail>(`/api/desa/${desaId}`),
    enabled: !!desaId,
  })
}

export function useUpdateDesa() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiFetch(`/api/desa/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['desa'] })
    },
  })
}

export function useDeactivateDesa() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch<{ ok: boolean }>(`/api/desa/${id}/deactivate`, { method: 'PATCH' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['desa'] })
    },
  })
}

export function useActivateDesa() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok?: boolean; error?: string; waConnected?: boolean }>(`/api/desa/${id}/activate`, {
        method: 'PATCH',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['desa'] })
      qc.invalidateQueries({ queryKey: ['session'] })
    },
  })
}

export function useCopyDesaConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      targetId,
      sourceDesaId,
      includeSuratHeader,
      includeTemplates,
    }: {
      targetId: string
      sourceDesaId: string
      includeSuratHeader?: boolean
      includeTemplates?: boolean
    }) =>
      apiFetch<{ ok: boolean; copiedFrom: string; templatesCopied: boolean }>(`/api/desa/${targetId}/copy-config`, {
        method: 'POST',
        body: JSON.stringify({ sourceDesaId, includeSuratHeader, includeTemplates }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['desa'] })
    },
  })
}

export function useToggleDesaAI() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, enabled, message }: { id: string; enabled: boolean; message?: string }) =>
      apiFetch<{ ok: boolean; ai_enabled: boolean; ai_disabled_message: string | null }>(`/api/desa/${id}/toggle-ai`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled, message }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['desa'] })
    },
  })
}

// Wilayah — cascading dropdown provinsi → kabupaten → kecamatan → desa
export function useProvinsi() {
  return useQuery({
    queryKey: ['wilayah', 'provinsi'],
    queryFn: () => apiFetch<Array<{ code: string; name: string }>>('/api/wilayah/provinsi'),
    staleTime: Number.POSITIVE_INFINITY, // data statis, tidak perlu refetch
  })
}

export function useKabupaten(provinsiCode: string | null) {
  return useQuery({
    queryKey: ['wilayah', 'kabupaten', provinsiCode],
    queryFn: () => apiFetch<Array<{ code: string; name: string }>>(`/api/wilayah/kabupaten?provinsi=${provinsiCode}`),
    enabled: !!provinsiCode,
    staleTime: Number.POSITIVE_INFINITY,
  })
}

export function useKecamatan(kabupatenCode: string | null) {
  return useQuery({
    queryKey: ['wilayah', 'kecamatan', kabupatenCode],
    queryFn: () => apiFetch<Array<{ code: string; name: string }>>(`/api/wilayah/kecamatan?kabupaten=${kabupatenCode}`),
    enabled: !!kabupatenCode,
    staleTime: Number.POSITIVE_INFINITY,
  })
}

export function useDesaWilayah(kecamatanCode: string | null) {
  return useQuery({
    queryKey: ['wilayah', 'desa', kecamatanCode],
    queryFn: () => apiFetch<Array<{ code: string; name: string }>>(`/api/wilayah/desa?kecamatan=${kecamatanCode}`),
    enabled: !!kecamatanCode,
    staleTime: Number.POSITIVE_INFINITY,
  })
}

// Buat desa baru (onboarding)
export function useCreateDesa() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      nama: string
      slug?: string
      kecamatan?: string
      kabupaten?: string
      provinsi?: string
      labelWilayah?: string
    }) =>
      apiFetch<{ id: string; slug: string; nama: string }>('/api/desa', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['session'] })
      qc.invalidateQueries({ queryKey: ['desa'] })
    },
  })
}

// Channel — WhatsApp & Telegram connection
export function useWaStatus(desaId?: string | null) {
  return useQuery({
    queryKey: ['channel', 'whatsapp', desaId],
    queryFn: () =>
      apiFetch<{ connected: boolean; instanceName: string | null; state?: string }>('/api/channel/whatsapp/status', {
        desaId,
      }),
    enabled: !!desaId,
  })
}

export function useConnectWa(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiFetch<{
        instanceName: string
        qrCode: string | null
        pairingCode: string | null
        status: string
        error?: string
      }>('/api/channel/whatsapp/connect', { method: 'POST', desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channel', 'whatsapp'] }),
  })
}

export function useWaQr(desaId?: string | null) {
  return useMutation({
    mutationFn: () =>
      apiFetch<{ qrCode: string | null; pairingCode: string | null; error?: string }>('/api/channel/whatsapp/qr', {
        desaId,
      }),
  })
}

export function useWaPairingCode(desaId?: string | null) {
  return useMutation({
    mutationFn: (phoneNumber: string) =>
      apiFetch<{
        instanceName?: string
        pairingCode?: string | null
        status?: string
        error?: string
      }>('/api/channel/whatsapp/pairing-code', {
        method: 'POST',
        body: JSON.stringify({ phoneNumber }),
        desaId,
      }),
  })
}

export function useDisconnectWa(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiFetch('/api/channel/whatsapp/disconnect', { method: 'POST', desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channel', 'whatsapp'] }),
  })
}

export function useRefreshWaNumber(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean; waNumber: string | null; error?: string }>('/api/channel/whatsapp/refresh-number', {
        method: 'POST',
        desaId,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['desa'] }),
  })
}

export function useTelegramStatus(desaId?: string | null) {
  return useQuery({
    queryKey: ['channel', 'telegram', desaId],
    queryFn: () =>
      apiFetch<{
        connected: boolean
        webhookActive?: boolean
        botUsername?: string
        botName?: string
        notifGroupId?: string | null
        error?: string
      }>('/api/channel/telegram/status', { desaId }),
    enabled: !!desaId,
  })
}

export function useConnectTelegram(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (botToken: string) =>
      apiFetch<{ ok?: boolean; botUsername?: string; botName?: string; error?: string }>(
        '/api/channel/telegram/connect',
        {
          method: 'POST',
          body: JSON.stringify({ botToken }),
          desaId,
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channel', 'telegram'] }),
  })
}

export function useDisconnectTelegram(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiFetch('/api/channel/telegram/disconnect', { method: 'POST', desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['channel', 'telegram'] }),
  })
}

export function useSetNotifGroup(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (groupId: string) =>
      apiFetch<{ ok?: boolean; error?: string }>('/api/channel/telegram/notification-group', {
        method: 'POST',
        body: JSON.stringify({ groupId }),
        desaId,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['desa'] }),
  })
}

// ─── Test Koneksi & Kirim Pesan ───

export function useTestWa(desaId?: string | null) {
  return useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean; latency: number; state?: string; instance?: string; error?: string | null }>(
        '/api/channel/whatsapp/test',
        { method: 'POST', desaId },
      ),
  })
}

export function useTestWaSend(desaId?: string | null) {
  return useMutation({
    mutationFn: (data: { phone: string; message?: string }) =>
      apiFetch<{ ok: boolean; latency: number; error?: string }>('/api/channel/whatsapp/test-send', {
        method: 'POST',
        body: JSON.stringify(data),
        desaId,
      }),
  })
}

export function useTestTelegram(desaId?: string | null) {
  return useMutation({
    mutationFn: () =>
      apiFetch<{
        ok: boolean
        latency: number
        botUsername?: string
        webhookActive?: boolean
        pendingUpdates?: number
        error?: string
      }>('/api/channel/telegram/test', { method: 'POST', desaId }),
  })
}

export function useTestTelegramSend(desaId?: string | null) {
  return useMutation({
    mutationFn: (data: { chatId: string; message?: string }) =>
      apiFetch<{ ok: boolean; latency: number; error?: string }>('/api/channel/telegram/test-send', {
        method: 'POST',
        body: JSON.stringify(data),
        desaId,
      }),
  })
}

export function useWebhookHealth(desaId?: string | null) {
  return useQuery({
    queryKey: ['channel', 'webhook-health', desaId],
    queryFn: () =>
      apiFetch<{
        whatsapp: { lastHit: number; agoMs: number } | null
        telegram: { lastHit: number; agoMs: number } | null
      }>('/api/channel/webhook-health', { desaId }),
    enabled: !!desaId,
    refetchInterval: 60_000, // poll tiap 1 menit
  })
}

// Percakapan handoff
export function usePercakapan(status = 'active', desaId?: string | null) {
  return useQuery({
    queryKey: ['percakapan', status, desaId],
    queryFn: () => apiFetch<PercakapanItem[]>(`/api/percakapan?status=${status}`, { desaId }),
    enabled: !!desaId,
    refetchInterval: 10000,
    placeholderData: keepPreviousData,
  })
}

export function usePercakapanDetail(id: string | null, desaId?: string | null) {
  return useQuery({
    queryKey: ['percakapan', id, desaId],
    queryFn: () => apiFetch<PercakapanDetail>(`/api/percakapan/${id}`, { desaId }),
    enabled: !!id && !!desaId,
  })
}

export function useReplyPercakapan(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) =>
      apiFetch(`/api/percakapan/${id}/reply`, { method: 'POST', body: JSON.stringify({ text }), desaId }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['percakapan', vars.id] })
    },
  })
}

export function useSendFilePercakapan(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, file, caption }: { id: string; file: File; caption?: string }) => {
      const formData = new FormData()
      formData.append('file', file)
      if (caption) formData.append('caption', caption)

      const headers: Record<string, string> = {}
      if (desaId) headers['X-Desa-Id'] = desaId

      const res = await fetch(`/api/percakapan/${id}/send-file`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: formData,
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Upload gagal' }))
        throw new Error(err.message || `HTTP ${res.status}`)
      }
      return res.json()
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['percakapan', vars.id] })
    },
  })
}

export function useSendBlangkoPercakapan(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, templateId }: { id: string; templateId: string }) =>
      apiFetch<{ ok?: boolean; error?: string; fileName?: string; templateNama?: string }>(
        `/api/percakapan/${id}/send-blangko`,
        { method: 'POST', body: JSON.stringify({ templateId }), desaId },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['percakapan', vars.id] })
    },
  })
}

export function useSelesaiPercakapan(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/percakapan/${id}/selesai`, { method: 'PATCH', desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['percakapan'] })
    },
  })
}

// Chat History — arsip riwayat percakapan
export function useChatHistory(wargaId?: string, desaId?: string | null, page = 1, limit = 20) {
  return useQuery({
    queryKey: ['percakapan', 'history', desaId, wargaId, page, limit],
    queryFn: () =>
      apiFetch<Paginated<ChatHistoryItem>>(
        `/api/percakapan/history?wargaId=${wargaId ?? ''}&page=${page}&limit=${limit}`,
        { desaId },
      ),
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

export function useChatHistoryDetail(id: string, desaId?: string | null) {
  return useQuery({
    queryKey: ['percakapan', 'history', id, desaId],
    queryFn: () => apiFetch<ChatHistoryItem>(`/api/percakapan/history/${id}`, { desaId }),
    enabled: !!id && !!desaId,
  })
}

// ─── Memory Desa ─────────────────────────────────────
export function useDesaMemories(desaId?: string | null, category?: string) {
  const params = new URLSearchParams()
  if (category) params.set('category', category)
  return useQuery({
    queryKey: ['memory', 'desa', desaId, category],
    queryFn: () => apiFetch<any[]>(`/api/memory/desa?${params}`, { desaId }),
    enabled: !!desaId,
  })
}

export function useCreateDesaMemory(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { content: string; category?: string }) =>
      apiFetch('/api/memory/desa', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['memory', 'desa', desaId] }),
  })
}

export function useUpdateDesaMemory(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiFetch(`/api/memory/desa/${id}`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['memory', 'desa', desaId] }),
  })
}

export function useDeleteDesaMemory(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/memory/desa/${id}`, { method: 'DELETE', desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['memory', 'desa', desaId] }),
  })
}

// ─── Memory Warga ────────────────────────────────────
export function useWargaMemories(desaId?: string | null, wargaId?: string) {
  const params = new URLSearchParams()
  if (wargaId) params.set('wargaId', wargaId)
  return useQuery({
    queryKey: ['memory', 'warga', desaId, wargaId],
    queryFn: () => apiFetch<any[]>(`/api/memory/warga?${params}`, { desaId }),
    enabled: !!desaId,
  })
}

export function useCreateWargaMemory(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { warga_id: string; content: string; category?: string }) =>
      apiFetch('/api/memory/warga', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['memory', 'warga', desaId] }),
  })
}

export function useUpdateWargaMemory(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiFetch(`/api/memory/warga/${id}`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['memory', 'warga', desaId] }),
  })
}

export function useDeleteWargaMemory(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/memory/warga/${id}`, { method: 'DELETE', desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['memory', 'warga', desaId] }),
  })
}

// ─── Surat Template ─────────────────────────────────
export function useSuratTemplates(desaId?: string | null) {
  return useQuery({
    queryKey: ['surat-template', desaId],
    queryFn: () => apiFetch<any[]>('/api/surat-template', { desaId }),
    enabled: !!desaId,
  })
}

export function useSuratTemplateDetail(id: string | null, desaId?: string | null) {
  return useQuery({
    queryKey: ['surat-template', id, desaId],
    queryFn: () => apiFetch<any>(`/api/surat-template/${id}`, { desaId }),
    enabled: !!id && !!desaId,
  })
}

export function useSuratTemplatePresets(desaId?: string | null) {
  return useQuery({
    queryKey: ['surat-template-presets'],
    queryFn: () =>
      apiFetch<
        {
          kode: string
          nama: string
          kategori: string
          deskripsi: string
          fields: { name: string; label: string; type: string; required: boolean }[]
          body_template: string
        }[]
      >('/api/surat-template/presets', { desaId }),
    enabled: !!desaId,
    staleTime: Number.POSITIVE_INFINITY, // data statis, tak perlu refetch
  })
}

export function useCreateSuratTemplate(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiFetch('/api/surat-template', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['surat-template', desaId] }),
  })
}

export function useUpdateSuratTemplate(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiFetch(`/api/surat-template/${id}`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['surat-template', desaId] }),
  })
}

export function useDeleteSuratTemplate(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/surat-template/${id}`, { method: 'DELETE', desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['surat-template', desaId] }),
  })
}

export function useToggleSuratTemplate(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/surat-template/${id}/toggle`, { method: 'PATCH', desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['surat-template', desaId] }),
  })
}

export function useReorderSuratTemplates(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (items: Array<{ id: string }>) =>
      apiFetch('/api/surat-template/reorder', { method: 'PATCH', body: JSON.stringify({ items }), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['surat-template', desaId] }),
  })
}

export function useUploadBlangko(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ templateId, file }: { templateId: string; file: File }) => {
      const formData = new FormData()
      formData.append('file', file)
      const headers: Record<string, string> = {}
      if (desaId) headers['X-Desa-Id'] = desaId
      const res = await fetch(`/api/surat-template/${templateId}/blangko`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: formData,
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Upload gagal' }))
        throw new Error(err.message || `HTTP ${res.status}`)
      }
      return res.json()
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['surat-template', desaId] }),
  })
}

// ─── wwebjs-api Session Management (platform_admin) ─────────

export interface WwebjsSession {
  id: string
  state: string
  status?: string
  success?: boolean
}

export function useWwebjsSessions() {
  return useQuery({
    queryKey: ['wwebjs', 'sessions'],
    queryFn: () => apiFetch<{ ok: boolean; data: unknown; error?: string }>('/api/admin/wwebjs/sessions'),
    refetchInterval: 15000,
  })
}

export function useWwebjsSessionStatus(sessionId: string | null) {
  return useQuery({
    queryKey: ['wwebjs', 'session', sessionId],
    queryFn: () =>
      apiFetch<{ ok: boolean; data: { success: boolean; state: string | null }; error?: string }>(
        `/api/admin/wwebjs/sessions/${sessionId}/status`,
      ),
    enabled: !!sessionId,
  })
}

export function useWwebjsSessionQr(sessionId: string | null) {
  return useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean; qrCode: string | null; error?: string }>(`/api/admin/wwebjs/sessions/${sessionId}/qr`),
  })
}

export function useWwebjsStartSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (sessionId: string) =>
      apiFetch<{ ok: boolean; data?: unknown; error?: string }>(`/api/admin/wwebjs/sessions/${sessionId}/start`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wwebjs'] }),
  })
}

export function useWwebjsCreateSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (sessionId: string) =>
      apiFetch<{ ok: boolean; data?: unknown; error?: string }>('/api/admin/wwebjs/sessions', {
        method: 'POST',
        body: JSON.stringify({ sessionId }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wwebjs'] }),
  })
}

export function useWwebjsTerminate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (sessionId: string) =>
      apiFetch<{ ok: boolean; error?: string }>(`/api/admin/wwebjs/sessions/${sessionId}/terminate`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wwebjs'] }),
  })
}

export function useWwebjsRestart() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (sessionId: string) =>
      apiFetch<{ ok: boolean; error?: string }>(`/api/admin/wwebjs/sessions/${sessionId}/restart`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wwebjs'] }),
  })
}

export function useWwebjsTerminateInactive() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiFetch<{ ok: boolean; error?: string }>('/api/admin/wwebjs/terminate-inactive', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wwebjs'] }),
  })
}

export function useWwebjsTerminateAll() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiFetch<{ ok: boolean; error?: string }>('/api/admin/wwebjs/terminate-all', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wwebjs'] }),
  })
}

// ─── Wwebjs Enhanced ───

export function useWwebjsServerHealth() {
  return useQuery({
    queryKey: ['wwebjs', 'server', 'health'],
    queryFn: () =>
      apiFetch<{
        ok: boolean
        reachable: boolean
        latencyMs: number
        serverUrl?: string
        sessionCount?: number
        byState?: Record<string, number>
        sessions?: Array<{ id: string; state: string }>
        error?: string
      }>('/api/admin/wwebjs/server/health'),
    refetchInterval: 30_000,
  })
}

export function useWwebjsAnomalies() {
  return useQuery({
    queryKey: ['wwebjs', 'anomalies'],
    queryFn: () =>
      apiFetch<{
        ok: boolean
        anomalies: Array<{
          type: 'orphan' | 'missing' | 'inactive_connected' | 'stale_webhook'
          severity: 'critical' | 'warning' | 'info'
          sessionId?: string
          desaId?: string
          desaNama?: string
          detail: string
        }>
        error?: string
      }>('/api/admin/wwebjs/anomalies'),
    refetchInterval: 30_000,
  })
}

export function useWwebjsSessionDetail(sessionId: string | null) {
  return useQuery({
    queryKey: ['wwebjs', 'session-detail', sessionId],
    queryFn: () =>
      apiFetch<{
        ok: boolean
        data: {
          sessionId: string
          status: unknown
          clientInfo: unknown
          wwebVersion: unknown
          clientState: unknown
          desa: { id: string; nama: string; slug: string; is_active: boolean } | null
          lastWebhook: number | null
        } | null
        error?: string
      }>(`/api/admin/wwebjs/sessions/${sessionId}/detail`),
    enabled: !!sessionId,
  })
}

export function useWwebjsScreenshot() {
  return useMutation({
    mutationFn: (sessionId: string) =>
      apiFetch<{ ok: boolean; image: string | null; error?: string }>(
        `/api/admin/wwebjs/sessions/${sessionId}/screenshot`,
      ),
  })
}

export function useWwebjsPairingCode() {
  return useMutation({
    mutationFn: (data: { sessionId: string; phoneNumber: string }) =>
      apiFetch<{ ok: boolean; data?: unknown; error?: string }>(
        `/api/admin/wwebjs/sessions/${data.sessionId}/pairing-code`,
        { method: 'POST', body: JSON.stringify({ phoneNumber: data.phoneNumber }) },
      ),
  })
}

export function useWwebjsSendTest() {
  return useMutation({
    mutationFn: (data: { sessionId: string; chatId: string; message?: string }) =>
      apiFetch<{ ok: boolean; data?: unknown; error?: string }>(
        `/api/admin/wwebjs/sessions/${data.sessionId}/send-test`,
        { method: 'POST', body: JSON.stringify({ chatId: data.chatId, message: data.message }) },
      ),
  })
}

export function useDeleteBlangko(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (templateId: string) =>
      apiFetch(`/api/surat-template/${templateId}/blangko`, { method: 'DELETE', desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['surat-template', desaId] }),
  })
}

export function useSeedPresets(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiFetch(`/api/desa/${desaId}/seed-presets`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['surat-template', desaId] }),
  })
}

export function useBlangkoPreview(desaId: string | null, templateId: string | null) {
  return useQuery({
    queryKey: ['blangko-preview', desaId, templateId],
    queryFn: () =>
      apiFetch(`/api/surat-template/${templateId}/blangko`, { desaId }) as Promise<{
        url: string
        mimetype: string
        filename: string
      }>,
    enabled: !!desaId && !!templateId,
    staleTime: 50 * 60 * 1000, // 50 menit (URL berlaku 1 jam)
  })
}

// ─── Desa Info (Knowledge Base) ─────────────────────

export function useDesaInfo(desaId?: string | null, kategori?: string) {
  const params = new URLSearchParams()
  if (kategori) params.set('kategori', kategori)
  const qs = params.toString()
  return useQuery({
    queryKey: ['desa-info', desaId, kategori],
    queryFn: () => apiFetch<any[]>(`/api/info${qs ? `?${qs}` : ''}`, { desaId }),
    enabled: !!desaId,
  })
}

export function useCreateDesaInfo(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiFetch('/api/info', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['desa-info', desaId] }),
  })
}

export function useUpdateDesaInfo(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiFetch(`/api/info/${id}`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['desa-info', desaId] }),
  })
}

export function useDeleteDesaInfo(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/info/${id}`, { method: 'DELETE', desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['desa-info', desaId] }),
  })
}

export function useToggleDesaInfo(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/info/${id}/toggle`, { method: 'PATCH', desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['desa-info', desaId] }),
  })
}

export function usePinDesaInfo(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/info/${id}/pin`, { method: 'PATCH', desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['desa-info', desaId] }),
  })
}

export function useSeedDesaInfo(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiFetch('/api/info/seed', { method: 'POST', desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['desa-info', desaId] }),
  })
}

// ─── User Management (Admin Global) ────────────────

export interface UsersFilters {
  status?: string
  desa_id?: string
  role?: string
  search?: string
  page?: number
  limit?: number
}

export interface UsersPaginatedResponse {
  data: any[]
  total: number
  page: number
  limit: number
  totalPages: number
}

export function useUsers(filters?: UsersFilters) {
  const params = new URLSearchParams()
  if (filters?.status) params.set('status', filters.status)
  if (filters?.desa_id) params.set('desa_id', filters.desa_id)
  if (filters?.role) params.set('role', filters.role)
  if (filters?.search) params.set('search', filters.search)
  if (filters?.page) params.set('page', String(filters.page))
  if (filters?.limit) params.set('limit', String(filters.limit))
  return useQuery({
    queryKey: ['users', filters],
    queryFn: () => apiFetch<UsersPaginatedResponse>(`/api/users?${params}`),
  })
}

export function usePendingUserCount(enabled = true) {
  return useQuery({
    queryKey: ['users', 'pending-count'],
    queryFn: () => apiFetch<{ count: number }>('/api/users/pending-count'),
    refetchInterval: 30_000,
    enabled,
  })
}

export function useSelectDesa() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (desaId: string) =>
      apiFetch('/api/users/select-desa', { method: 'POST', body: JSON.stringify({ desaId }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['session'] }),
  })
}

// ─── Admin Management ─────────────────────────

export function useRequestAdmin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiFetch('/api/users/request-admin', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['session'] }),
  })
}

export function useAdminList(filter?: string, search?: string, page = 1) {
  const params = new URLSearchParams()
  if (filter) params.set('filter', filter)
  if (search) params.set('search', search)
  params.set('page', String(page))
  return useQuery({
    queryKey: ['admin-list', filter, search, page],
    queryFn: () =>
      apiFetch<{
        data: Array<{
          id: string
          name: string
          email: string
          image: string | null
          role: string
          status: string
          admin_request: boolean
          approved_by: string | null
          approved_at: string | null
          rejected_reason: string | null
          managed_desa_ids: string[] | null
          is_super_admin: boolean
          createdAt: string
          approver: { id: string; name: string; email: string } | null
        }>
        total: number
        page: number
        limit: number
        totalPages: number
      }>(`/api/users/admin-list?${params}`),
  })
}

export function useAdminRequestCount() {
  return useQuery({
    queryKey: ['admin-request-count'],
    queryFn: () => apiFetch<{ count: number }>('/api/users/admin-request-count'),
    refetchInterval: 60_000,
  })
}

export function useApproveAdmin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/users/${id}/approve-admin`, { method: 'PUT' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-list'] })
      qc.invalidateQueries({ queryKey: ['admin-request-count'] })
    },
  })
}

export function useRejectAdmin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      apiFetch(`/api/users/${id}/reject-admin`, { method: 'PUT', body: JSON.stringify({ reason }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-list'] })
      qc.invalidateQueries({ queryKey: ['admin-request-count'] })
    },
  })
}

export function useDemoteAdmin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/users/${id}/demote-admin`, { method: 'PUT' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-list'] }),
  })
}

// ─── QC Management ─────────────────────────

export function useQcList(search?: string, page = 1) {
  const params = new URLSearchParams()
  if (search) params.set('search', search)
  params.set('page', String(page))
  return useQuery({
    queryKey: ['qc-list', search, page],
    queryFn: () =>
      apiFetch<{
        data: Array<{
          id: string
          name: string
          email: string
          image: string | null
          role: string
          status: string
          createdAt: string
        }>
        total: number
        page: number
        limit: number
        totalPages: number
      }>(`/api/users/qc-list?${params}`),
  })
}

export function usePromoteQc() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/users/${id}/promote-qc`, { method: 'PUT' }),
    // invalidate ['users'] juga: toggle QC dipicu dari AllUsersSection (queryKey ['users']),
    // bukan hanya QCSection (['qc-list']) — tanpa ini badge/tombol QC tetap stale.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['qc-list'] })
      qc.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export function useDemoteQc() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/users/${id}/demote-qc`, { method: 'PUT' }),
    // invalidate ['users'] juga: lihat catatan usePromoteQc.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['qc-list'] })
      qc.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export function useUpdateManagedDesas() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, desaIds }: { id: string; desaIds: string[] | null }) =>
      apiFetch(`/api/users/${id}/managed-desas`, {
        method: 'PUT',
        body: JSON.stringify({ desaIds }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-list'] }),
  })
}

export function useApproveUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, permissions }: { id: string; permissions?: string[] }) =>
      apiFetch(`/api/users/${id}/approve`, { method: 'PUT', body: JSON.stringify({ permissions }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export function useRejectUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      apiFetch(`/api/users/${id}/reject`, { method: 'PUT', body: JSON.stringify({ reason }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export function useSuspendUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/users/${id}/suspend`, { method: 'PUT' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })
}

export function useReactivateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/users/${id}/reactivate`, { method: 'PUT' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })
}

export function useUpdateUserPermissions() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, permissions }: { id: string; permissions: string[] }) =>
      apiFetch(`/api/users/${id}/permissions`, { method: 'PUT', body: JSON.stringify({ permissions }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })
}

export function useUpdateUserRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) =>
      apiFetch(`/api/users/${id}/role`, { method: 'PUT', body: JSON.stringify({ role }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })
}

export function useUpdateUserDesa() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, desaId }: { id: string; desaId: string }) =>
      apiFetch(`/api/users/${id}/desa`, { method: 'PUT', body: JSON.stringify({ desaId }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })
}

// ─── Staff User Management (delegasi berjenjang) ────

export function useStaffUsers(desaId?: string | null) {
  const params = desaId ? `?desa_id=${desaId}` : ''
  return useQuery({
    queryKey: ['staff-users', desaId],
    queryFn: () => apiFetch<{ data: any[] }>(`/api/staff-users${params}`),
    enabled: !!desaId,
  })
}

export function useStaffPendingCount() {
  return useQuery({
    queryKey: ['staff-users', 'pending-count'],
    queryFn: () => apiFetch<{ count: number }>('/api/staff-users/pending-count'),
    refetchInterval: 60_000,
  })
}

export function useAssignableRoles() {
  return useQuery({
    queryKey: ['assignable-roles'],
    queryFn: () => apiFetch<{ roles: { value: string; label: string }[] }>('/api/staff-users/assignable-roles'),
  })
}

export function useStaffUpdateRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) =>
      apiFetch(`/api/staff-users/${id}/role`, { method: 'PUT', body: JSON.stringify({ role }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['staff-users'] }),
  })
}

export function useStaffApproveUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/staff-users/${id}/approve`, { method: 'PUT' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['staff-users'] }),
  })
}

export function useStaffRejectUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, alasan }: { id: string; alasan?: string }) =>
      apiFetch(`/api/staff-users/${id}/reject`, { method: 'PUT', body: JSON.stringify({ alasan }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['staff-users'] }),
  })
}

export function useStaffRestoreUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/staff-users/${id}/restore`, { method: 'PUT' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['staff-users'] }),
  })
}

export function useStaffSuspendUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/staff-users/${id}/suspend`, { method: 'PUT' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['staff-users'] }),
  })
}

export function useStaffReactivateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/staff-users/${id}/reactivate`, { method: 'PUT' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['staff-users'] }),
  })
}

// ─── Kepala Desa Management ───────────────────────────

export function useKepalaDesaList() {
  return useQuery({
    queryKey: ['users', 'kepala-desa'],
    queryFn: () =>
      apiFetch<
        Array<{
          desaId: string
          desaNama: string
          desaSlug: string
          desaActive: boolean
          kepala: { id: string; name: string; email: string; image?: string; approvedAt?: string } | null
        }>
      >('/api/users/kepala-desa'),
  })
}

export function useKepalaDesaCandidates(desaId?: string | null) {
  return useQuery({
    queryKey: ['users', 'kepala-desa', 'candidates', desaId],
    queryFn: () =>
      apiFetch<Array<{ id: string; name: string; email: string; role: string }>>(
        `/api/users/kepala-desa/candidates/${desaId}`,
      ),
    enabled: !!desaId,
  })
}

export function useTransferKepala() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { desaId: string; newKepalaId: string; demoteToRole?: string }) =>
      apiFetch('/api/users/kepala-desa/transfer', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export function useDemoteKepala() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { desaId: string; demoteToRole?: string }) =>
      apiFetch('/api/users/kepala-desa/demote', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

export function useKepalaStaffCandidates(desaId?: string | null) {
  const params = desaId ? `?desaId=${desaId}` : ''
  return useQuery({
    queryKey: ['staff-users', 'kepala-candidates', desaId ?? 'self'],
    queryFn: () =>
      apiFetch<{ candidates: Array<{ id: string; name: string; email: string; role: string; image?: string }> }>(
        `/api/staff-users/kepala-candidates${params}`,
      ),
  })
}

export function useKepalaStaffTransfer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { desaId: string; newKepalaId: string; demoteToRole?: string }) =>
      apiFetch('/api/staff-users/kepala-transfer', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['staff-users'] })
      qc.invalidateQueries({ queryKey: ['users'] })
    },
  })
}

// ─── Intervention ─────────────────────────────────────

export function useInterventionStart() {
  return useMutation({
    mutationFn: (desaId: string) => apiFetch(`/api/desa/${desaId}/intervention/start`, { method: 'POST' }),
  })
}

export function useInterventionHeartbeat() {
  return useMutation({
    mutationFn: (desaId: string) => apiFetch(`/api/desa/${desaId}/intervention/heartbeat`, { method: 'POST' }),
  })
}

export function useInterventionEnd() {
  return useMutation({
    mutationFn: (desaId: string) => apiFetch(`/api/desa/${desaId}/intervention/end`, { method: 'POST' }),
  })
}

export function useInterventionStatus(desaId?: string | null) {
  return useQuery({
    queryKey: ['intervention-status', desaId],
    queryFn: () =>
      apiFetch<{ active: boolean; adminName?: string; startedAt?: string }>(`/api/desa/${desaId}/intervention/status`),
    enabled: !!desaId,
    refetchInterval: 5_000,
    staleTime: 0,
    gcTime: 0,
  })
}

// ─── Public API ─────────────────────────────────────

async function publicFetch<T>(url: string): Promise<T> {
  const r = await fetch(url)
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

export function usePublicLaporanList(slug?: string, status?: string, page?: number) {
  return useQuery({
    queryKey: ['public', 'laporan', slug, status, page],
    queryFn: () => {
      const params = new URLSearchParams()
      if (status) params.set('status', status)
      if (page) params.set('page', String(page))
      return publicFetch<{
        data: Array<{
          id: string
          kategori: string
          deskripsi: string
          lokasi: string | null
          status: string
          priority: string
          has_foto: boolean
          has_video: boolean
          deadline: string | null
          resolved_at: string | null
          rating: number | null
          progress_count: number
          created_at: string
        }>
        total: number
        page: number
        limit: number
        desa_nama: string
      }>(`/api/public/desa/${slug}/laporan?${params}`)
    },
    enabled: !!slug,
    placeholderData: keepPreviousData,
  })
}

export function usePublicLaporanDetail(slug?: string, id?: string) {
  return useQuery({
    queryKey: ['public', 'laporan', slug, id],
    queryFn: () =>
      publicFetch<{
        id: string
        kategori: string
        deskripsi: string
        lokasi: string | null
        status: string
        priority: string
        deadline: string | null
        resolved_at: string | null
        rating: number | null
        feedback: string | null
        created_at: string
        media: { fotos: string[]; video: string | null; thumbnail: string | null }
        logs: Array<{
          id: string
          action: string
          from_status: string | null
          to_status: string | null
          note: string | null
          progressFotoUrl: string | null
          actor_name: string | null
          created_at: string
        }>
        desa_nama: string
      }>(`/api/public/desa/${slug}/laporan/${id}`),
    enabled: !!slug && !!id,
  })
}

export function usePublicLaporanStats(slug?: string) {
  return useQuery({
    queryKey: ['public', 'laporan-stats', slug],
    queryFn: () =>
      publicFetch<{
        total: number
        selesai: number
        kadaluarsa: number
        bulan_ini: number
        avg_rating: number | null
        total_rated: number
        avg_resolution_hours: number | null
      }>(`/api/public/desa/${slug}/laporan/stats`),
    enabled: !!slug,
  })
}

export function usePublicDesaList() {
  return useQuery({
    queryKey: ['public', 'desa'],
    queryFn: () =>
      apiFetch<
        Array<{
          id: string
          slug: string
          nama: string
          kecamatan?: string
          kabupaten?: string
          provinsi?: string
          logo_url?: string
          nama_ai: string
        }>
      >('/api/public/desa'),
  })
}

export function usePublicDesa(slug?: string) {
  return useQuery({
    queryKey: ['public', 'desa', slug],
    queryFn: () => apiFetch<any>(`/api/public/desa/${slug}`),
    enabled: !!slug,
  })
}

export function usePublicDesaStats(slug?: string) {
  return useQuery({
    queryKey: ['public', 'desa', slug, 'stats'],
    queryFn: () =>
      apiFetch<{ warga: number; surat: number; laporan: number; laporan_selesai: number }>(
        `/api/public/desa/${slug}/stats`,
      ),
    enabled: !!slug,
  })
}

// Logout — redirect via window.location karena perlu clear session cookies
export function useLogout() {
  return useMutation({
    mutationFn: () => apiFetch('/api/auth/sign-out', { method: 'POST' }),
    onSuccess: () => {
      window.location.href = '/login'
    },
  })
}

// ─── TTD (Tanda Tangan) Upload ───────────────────────

export function useUploadTTD() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ desaId, role, file }: { desaId: string; role: 'kepala' | 'sekretaris'; file: File }) => {
      const fd = new FormData()
      fd.append('file', file)
      const res = await fetch(`/api/desa/${desaId}/ttd/${role}`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Upload gagal' }))
        throw new Error(err.message)
      }
      return res.json() as Promise<{ ok: boolean; url: string }>
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['desa'] })
    },
  })
}

export function useDeleteTTD() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ desaId, role }: { desaId: string; role: 'kepala' | 'sekretaris' }) => {
      const res = await fetch(`/api/desa/${desaId}/ttd/${role}`, {
        method: 'DELETE',
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Gagal menghapus TTD')
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['desa'] })
    },
  })
}

export function useUploadLogo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ desaId, file }: { desaId: string; file: File }) => {
      const fd = new FormData()
      fd.append('file', file)
      const res = await fetch(`/api/desa/${desaId}/logo`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Upload gagal' }))
        throw new Error(err.message)
      }
      return res.json() as Promise<{ ok: boolean; url: string }>
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['desa'] })
    },
  })
}

export function useDeleteLogo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ desaId }: { desaId: string }) => {
      const res = await fetch(`/api/desa/${desaId}/logo`, {
        method: 'DELETE',
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Gagal menghapus logo')
      return res.json()
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['desa'] })
    },
  })
}

// ─── Surat Register (Buku Register Surat Masuk/Keluar) ───

export function useSuratRegister(
  desaId?: string | null,
  filters?: { tipe?: string; search?: string },
  page = 1,
  limit = 20,
) {
  const params = new URLSearchParams()
  params.set('page', String(page))
  params.set('limit', String(limit))
  if (filters?.tipe) params.set('tipe', filters.tipe)
  if (filters?.search) params.set('search', filters.search)
  return useQuery({
    queryKey: ['surat-register', desaId, filters?.tipe, filters?.search, page],
    queryFn: () => apiFetch<Paginated<any>>(`/api/surat-register?${params}`, { desaId }),
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

export function useCreateSuratRegister(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiFetch('/api/surat-register', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['surat-register', desaId] }),
  })
}

export function useUpdateSuratRegister(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiFetch(`/api/surat-register/${id}`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['surat-register', desaId] }),
  })
}

export function useDeleteSuratRegister(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/surat-register/${id}`, { method: 'DELETE', desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['surat-register', desaId] }),
  })
}

// ─── Warga Demografi Stats ──────────────────────────

export function useWargaDemografi(desaId?: string | null) {
  return useQuery({
    queryKey: ['warga', 'demografi', desaId],
    queryFn: () =>
      apiFetch<{
        total: number
        laki_laki: number
        perempuan: number
        jumlah_kk: number
        agama: Array<{ label: string; count: number }>
        pekerjaan: Array<{ label: string; count: number }>
        pendidikan: Array<{ label: string; count: number }>
        status_kawin: Array<{ label: string; count: number }>
      }>('/api/warga/stats/demografi', { desaId }),
    enabled: !!desaId,
  })
}

// ─── Kepegawaian ────────────────────────────────────

export function useAbsensi(desaId?: string | null, tanggal?: string) {
  const params = new URLSearchParams()
  if (tanggal) params.set('tanggal', tanggal)
  return useQuery({
    queryKey: ['kepegawaian', 'absensi', desaId, tanggal],
    queryFn: () => apiFetch<{ data: any[] }>(`/api/kepegawaian/absensi?${params}`, { desaId }),
    enabled: !!desaId,
  })
}

export function useCreateAbsensi(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiFetch('/api/kepegawaian/absensi', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kepegawaian', 'absensi'] }),
  })
}

export function useBatchAbsensi(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { tanggal: string; items: any[] }) =>
      apiFetch('/api/kepegawaian/absensi/batch', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kepegawaian', 'absensi'] }),
  })
}

export function useRekapAbsensi(desaId?: string | null, bulan?: number, tahun?: number) {
  const params = new URLSearchParams()
  if (bulan) params.set('bulan', String(bulan))
  if (tahun) params.set('tahun', String(tahun))
  return useQuery({
    queryKey: ['kepegawaian', 'absensi', 'rekap', desaId, bulan, tahun],
    queryFn: () => apiFetch<any>(`/api/kepegawaian/absensi/rekap?${params}`, { desaId }),
    enabled: !!desaId,
  })
}

export function useSiltap(desaId?: string | null, tahun?: number) {
  const params = new URLSearchParams()
  if (tahun) params.set('tahun', String(tahun))
  return useQuery({
    queryKey: ['kepegawaian', 'siltap', desaId, tahun],
    queryFn: () => apiFetch<{ data: any[] }>(`/api/kepegawaian/siltap?${params}`, { desaId }),
    enabled: !!desaId,
  })
}

export function useGenerateSiltap(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      bulan: number
      tahun: number
      // Mode per-karyawan (TKT-0089/0090) — tiap staff bisa beda gaji.
      items?: { user_id: string; gaji_pokok: number; tunjangan?: number; potongan?: number }[]
      // Mode broadcast lama (backward-compatible).
      gaji_pokok?: number
      tunjangan?: number
      potongan?: number
    }) => apiFetch('/api/kepegawaian/siltap/generate', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kepegawaian', 'siltap'] }),
  })
}

export function useUpdateSiltap(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiFetch(`/api/kepegawaian/siltap/${id}`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kepegawaian', 'siltap'] }),
  })
}

export function useCuti(desaId?: string | null, status?: string) {
  const params = new URLSearchParams()
  if (status) params.set('status', status)
  return useQuery({
    queryKey: ['kepegawaian', 'cuti', desaId, status],
    queryFn: () => apiFetch<{ data: any[] }>(`/api/kepegawaian/cuti?${params}`, { desaId }),
    enabled: !!desaId,
  })
}

export function useCreateCuti(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiFetch('/api/kepegawaian/cuti', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kepegawaian', 'cuti'] }),
  })
}

export function useUpdateCuti(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiFetch(`/api/kepegawaian/cuti/${id}`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kepegawaian', 'cuti'] }),
  })
}

// ─── Aset Desa ──────────────────────────────────────

export function useAsetDesa(
  desaId?: string | null,
  filters?: { kategori?: string; kondisi?: string; tahun?: string; search?: string },
  page = 1,
) {
  const params = new URLSearchParams()
  params.set('page', String(page))
  if (filters?.kategori) params.set('kategori', filters.kategori)
  if (filters?.kondisi) params.set('kondisi', filters.kondisi)
  if (filters?.tahun) params.set('tahun', filters.tahun)
  if (filters?.search) params.set('search', filters.search)
  return useQuery({
    queryKey: ['aset', desaId, filters?.kategori, filters?.kondisi, filters?.tahun, filters?.search, page],
    queryFn: () => apiFetch<Paginated<any>>(`/api/aset?${params}`, { desaId }),
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

export function useAsetTahunOptions(desaId?: string | null) {
  return useQuery({
    queryKey: ['aset', 'tahun-options', desaId],
    queryFn: () => apiFetch<number[]>('/api/aset/tahun-options', { desaId }),
    enabled: !!desaId,
  })
}

export function useAsetStats(desaId?: string | null) {
  return useQuery({
    queryKey: ['aset', 'stats', desaId],
    queryFn: () =>
      apiFetch<{ total: number; categories: Array<{ kategori: string; count: number }> }>('/api/aset/stats', {
        desaId,
      }),
    enabled: !!desaId,
  })
}

export function useCreateAset(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiFetch('/api/aset', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['aset', desaId] })
      qc.invalidateQueries({ queryKey: ['aset', 'stats', desaId] })
    },
  })
}

export function useUpdateAset(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiFetch(`/api/aset/${id}`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['aset', desaId] })
      qc.invalidateQueries({ queryKey: ['aset', 'stats', desaId] })
    },
  })
}

export function useDeleteAset(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/aset/${id}`, { method: 'DELETE', desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['aset', desaId] })
      qc.invalidateQueries({ queryKey: ['aset', 'stats', desaId] })
    },
  })
}

export function useUploadAsetFoto(desaId?: string | null) {
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData()
      fd.append('foto', file)
      const headers: Record<string, string> = {}
      if (desaId) headers['X-Desa-Id'] = desaId
      const res = await fetch('/api/aset/upload-foto', {
        method: 'POST',
        credentials: 'include',
        headers,
        body: fd,
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Gagal upload foto' }))
        throw new Error(err.message || `HTTP ${res.status}`)
      }
      return res.json() as Promise<{ foto_url: string }>
    },
  })
}

export function useAsetFotoUrl(desaId?: string | null, asetId?: string | null, hasFoto?: boolean) {
  return useQuery({
    queryKey: ['aset', 'foto-url', desaId, asetId],
    queryFn: () => apiFetch<{ url: string | null }>(`/api/aset/${asetId}/foto-url`, { desaId }),
    enabled: !!desaId && !!asetId && !!hasFoto,
    staleTime: 55 * 60 * 1000, // 55 menit — presigned URL valid 1 jam
  })
}

// ─── Keuangan Desa ──────────────────────────────────

export function useAnggaran(desaId?: string | null, tahun?: number) {
  const params = new URLSearchParams()
  if (tahun) params.set('tahun', String(tahun))
  return useQuery({
    queryKey: ['keuangan', 'anggaran', desaId, tahun],
    queryFn: () => apiFetch<{ data: any[]; tahun: number }>(`/api/keuangan/anggaran?${params}`, { desaId }),
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

export function useCreateAnggaran(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiFetch('/api/keuangan/anggaran', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keuangan', 'anggaran'] })
      qc.invalidateQueries({ queryKey: ['keuangan', 'ringkasan'] })
    },
  })
}

export function useUpdateAnggaran(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiFetch(`/api/keuangan/anggaran/${id}`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keuangan', 'anggaran'] })
      qc.invalidateQueries({ queryKey: ['keuangan', 'ringkasan'] })
    },
  })
}

export function useDeleteAnggaran(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/keuangan/anggaran/${id}`, { method: 'DELETE', desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keuangan', 'anggaran'] })
      qc.invalidateQueries({ queryKey: ['keuangan', 'ringkasan'] })
    },
  })
}

export function useTransaksiKas(
  desaId?: string | null,
  filters?: { jenis?: string; kategori?: string; tahun?: number },
  page = 1,
) {
  const params = new URLSearchParams()
  params.set('page', String(page))
  if (filters?.jenis) params.set('jenis', filters.jenis)
  if (filters?.kategori) params.set('kategori', filters.kategori)
  if (filters?.tahun) params.set('tahun', String(filters.tahun))
  return useQuery({
    queryKey: ['keuangan', 'kas', desaId, filters?.jenis, filters?.kategori, filters?.tahun, page],
    queryFn: () => apiFetch<Paginated<any>>(`/api/keuangan/kas?${params}`, { desaId }),
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

export function useCreateTransaksiKas(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiFetch('/api/keuangan/kas', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keuangan', 'kas'] })
      qc.invalidateQueries({ queryKey: ['keuangan', 'saldo'] })
      qc.invalidateQueries({ queryKey: ['keuangan', 'ringkasan'] })
      qc.invalidateQueries({ queryKey: ['keuangan', 'anggaran'] })
    },
  })
}

export function useUpdateTransaksiKas(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiFetch(`/api/keuangan/kas/${id}`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keuangan', 'kas'] })
      qc.invalidateQueries({ queryKey: ['keuangan', 'saldo'] })
      qc.invalidateQueries({ queryKey: ['keuangan', 'ringkasan'] })
    },
  })
}

export function useDeleteTransaksiKas(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/keuangan/kas/${id}`, { method: 'DELETE', desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['keuangan', 'kas'] })
      qc.invalidateQueries({ queryKey: ['keuangan', 'saldo'] })
      qc.invalidateQueries({ queryKey: ['keuangan', 'ringkasan'] })
    },
  })
}

export function useSaldoKas(desaId?: string | null, tahun?: number) {
  const params = tahun ? `?tahun=${tahun}` : ''
  return useQuery({
    queryKey: ['keuangan', 'saldo', desaId, tahun],
    queryFn: () =>
      apiFetch<{ saldo: number; total_masuk: number; total_keluar: number }>(`/api/keuangan/kas/saldo${params}`, {
        desaId,
      }),
    enabled: !!desaId,
  })
}

export function useRingkasanKeuangan(desaId?: string | null, tahun?: number) {
  const params = new URLSearchParams()
  if (tahun) params.set('tahun', String(tahun))
  return useQuery({
    queryKey: ['keuangan', 'ringkasan', desaId, tahun],
    queryFn: () => apiFetch<any>(`/api/keuangan/ringkasan?${params}`, { desaId }),
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

// ============================================================
// Pembangunan
// ============================================================

export function useProyekPembangunan(
  desaId?: string | null,
  filters?: { status?: string; kategori?: string; search?: string },
  page = 1,
) {
  const params = new URLSearchParams()
  params.set('page', String(page))
  if (filters?.status) params.set('status', filters.status)
  if (filters?.kategori) params.set('kategori', filters.kategori)
  if (filters?.search) params.set('search', filters.search)
  return useQuery({
    queryKey: ['pembangunan', desaId, filters, page],
    queryFn: () => apiFetch<{ data: any[]; total: number }>(`/api/pembangunan?${params}`, { desaId }),
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

export function useProyekDetail(desaId?: string | null, id?: string | null) {
  return useQuery({
    queryKey: ['pembangunan', 'detail', desaId, id],
    queryFn: () => apiFetch<any>(`/api/pembangunan/${id}`, { desaId }),
    enabled: !!desaId && !!id,
  })
}

export function useCreateProyek(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiFetch('/api/pembangunan', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pembangunan'] }),
  })
}

export function useUpdateProyek(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiFetch(`/api/pembangunan/${id}`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pembangunan'] }),
  })
}

export function useDeleteProyek(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/pembangunan/${id}`, { method: 'DELETE', desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pembangunan'] }),
  })
}

export function useCreateProyekLog(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      proyekId,
      data,
    }: {
      proyekId: string
      data: Record<string, unknown> & { foto?: File | null }
    }) => {
      const fd = new FormData()
      fd.append('jenis', String(data.jenis ?? ''))
      if (data.catatan) fd.append('catatan', String(data.catatan))
      if (data.progres != null) fd.append('progres', String(data.progres))
      if (data.foto instanceof File) fd.append('foto', data.foto)
      const headers: Record<string, string> = {}
      if (desaId) headers['X-Desa-Id'] = desaId
      const res = await fetch(`/api/pembangunan/${proyekId}/log`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: fd,
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Gagal tambah log' }))
        throw new Error(err.message || `HTTP ${res.status}`)
      }
      return res.json()
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pembangunan'] }),
  })
}

export function useProyekLogFotoUrl(
  desaId?: string | null,
  proyekId?: string | null,
  logId?: string | null,
  hasFoto?: boolean,
) {
  return useQuery({
    queryKey: ['pembangunan', 'log-foto-url', desaId, proyekId, logId],
    queryFn: () => apiFetch<{ url: string | null }>(`/api/pembangunan/${proyekId}/log/${logId}/foto-url`, { desaId }),
    enabled: !!desaId && !!proyekId && !!logId && !!hasFoto,
    staleTime: 55 * 60 * 1000, // 55 menit — presigned URL valid 1 jam
  })
}

export function usePembangunanStats(desaId?: string | null) {
  return useQuery({
    queryKey: ['pembangunan', 'stats', desaId],
    queryFn: () =>
      apiFetch<{ total: number; anggaran: number; realisasi: number; by_status: any[] }>('/api/pembangunan/stats', {
        desaId,
      }),
    enabled: !!desaId,
  })
}

// ============================================================
// Kesehatan
// ============================================================

export function useDataKesehatan(
  desaId?: string | null,
  filters?: { kategori?: string; status?: string; jenis_kelamin?: string; search?: string },
  page = 1,
) {
  const params = new URLSearchParams()
  params.set('page', String(page))
  if (filters?.kategori) params.set('kategori', filters.kategori)
  if (filters?.status) params.set('status', filters.status)
  if (filters?.jenis_kelamin) params.set('jenis_kelamin', filters.jenis_kelamin)
  if (filters?.search) params.set('search', filters.search)
  return useQuery({
    queryKey: ['kesehatan', desaId, filters, page],
    queryFn: () => apiFetch<{ data: any[]; total: number }>(`/api/kesehatan?${params}`, { desaId }),
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

export function useCreateKesehatan(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiFetch('/api/kesehatan', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kesehatan'] }),
    onError: () => notifications.show({ title: 'Gagal', message: 'Gagal menambahkan data kesehatan', color: 'red' }),
  })
}

export function useUpdateKesehatan(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiFetch(`/api/kesehatan/${id}`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kesehatan'] }),
    onError: () => notifications.show({ title: 'Gagal', message: 'Gagal memperbarui data kesehatan', color: 'red' }),
  })
}

export function useDeleteKesehatan(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/kesehatan/${id}`, { method: 'DELETE', desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kesehatan'] }),
    onError: () => notifications.show({ title: 'Gagal', message: 'Gagal menghapus data kesehatan', color: 'red' }),
  })
}

export function useKesehatanStats(desaId?: string | null) {
  return useQuery({
    queryKey: ['kesehatan', 'stats', desaId],
    queryFn: () => apiFetch<{ total: number; by_kategori: any[] }>('/api/kesehatan/stats', { desaId }),
    enabled: !!desaId,
  })
}

// ============================================================
// Pertanahan
// ============================================================

export function useRegisterTanah(
  desaId?: string | null,
  filters?: { status?: string; jenis_hak?: string; search?: string },
  page = 1,
) {
  const params = new URLSearchParams()
  params.set('page', String(page))
  if (filters?.status) params.set('status', filters.status)
  if (filters?.jenis_hak) params.set('jenis_hak', filters.jenis_hak)
  if (filters?.search) params.set('search', filters.search)
  return useQuery({
    queryKey: ['pertanahan', desaId, filters, page],
    queryFn: () => apiFetch<{ data: any[]; total: number }>(`/api/pertanahan?${params}`, { desaId }),
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

export function useCreateTanah(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiFetch('/api/pertanahan', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pertanahan'] }),
  })
}

export function useUpdateTanah(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiFetch(`/api/pertanahan/${id}`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pertanahan'] }),
  })
}

export function useDeleteTanah(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/pertanahan/${id}`, { method: 'DELETE', desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pertanahan'] }),
  })
}

// ============================================================
// Pendidikan
// ============================================================

export function useDataPendidikan(
  desaId?: string | null,
  filters?: { jenjang?: string; status?: string; tahun_ajar?: string; search?: string },
  page = 1,
) {
  const params = new URLSearchParams()
  params.set('page', String(page))
  if (filters?.jenjang) params.set('jenjang', filters.jenjang)
  if (filters?.status) params.set('status', filters.status)
  if (filters?.tahun_ajar) params.set('tahun_ajar', filters.tahun_ajar)
  if (filters?.search) params.set('search', filters.search)
  return useQuery({
    queryKey: ['pendidikan', desaId, filters, page],
    queryFn: () =>
      apiFetch<{ data: import('../types').DataPendidikanItem[]; total: number }>(`/api/pendidikan?${params}`, {
        desaId,
      }),
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

export function useCreatePendidikan(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiFetch('/api/pendidikan', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pendidikan'] }),
  })
}

export function useUpdatePendidikan(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiFetch(`/api/pendidikan/${id}`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pendidikan'] }),
  })
}

export function useDeletePendidikan(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/pendidikan/${id}`, { method: 'DELETE', desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pendidikan'] }),
  })
}

// ============================================================
// Ekonomi — BUMDes & Kelompok
// ============================================================

export function useBumdes(desaId?: string | null, status?: string, page = 1) {
  const params = new URLSearchParams()
  params.set('page', String(page))
  if (status) params.set('status', status)
  return useQuery({
    queryKey: ['ekonomi', 'bumdes', desaId, status, page],
    queryFn: () =>
      apiFetch<{ data: import('../types').BumdesItem[]; total: number }>(`/api/ekonomi/bumdes?${params}`, { desaId }),
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

export function useCreateBumdes(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiFetch('/api/ekonomi/bumdes', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ekonomi', 'bumdes'] }),
  })
}

export function useUpdateBumdes(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiFetch(`/api/ekonomi/bumdes/${id}`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ekonomi', 'bumdes'] }),
  })
}

export function useDeleteBumdes(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/ekonomi/bumdes/${id}`, { method: 'DELETE', desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ekonomi', 'bumdes'] }),
  })
}

export function useKelompokMasyarakat(desaId?: string | null, jenis?: string, page = 1, status?: string) {
  const params = new URLSearchParams()
  params.set('page', String(page))
  if (jenis) params.set('jenis', jenis)
  if (status) params.set('status', status)
  return useQuery({
    queryKey: ['ekonomi', 'kelompok', desaId, jenis, page, status],
    queryFn: () =>
      apiFetch<{ data: import('../types').KelompokItem[]; total: number }>(`/api/ekonomi/kelompok?${params}`, {
        desaId,
      }),
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

export function useCreateKelompok(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiFetch('/api/ekonomi/kelompok', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ekonomi', 'kelompok'] }),
  })
}

export function useUpdateKelompok(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiFetch(`/api/ekonomi/kelompok/${id}`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ekonomi', 'kelompok'] }),
  })
}

export function useDeleteKelompok(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/ekonomi/kelompok/${id}`, { method: 'DELETE', desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ekonomi', 'kelompok'] }),
  })
}

// ============================================================
// Ketertiban — Siskamling & Bencana
// ============================================================

export function useSiskamling(desaId?: string | null, page = 1, status?: string) {
  return useQuery({
    queryKey: ['ketertiban', 'siskamling', desaId, page, status],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page) })
      if (status) params.set('status', status)
      return apiFetch<{ data: import('../types').JadwalSiskamlingItem[]; total: number }>(
        `/api/ketertiban/siskamling?${params}`,
        { desaId },
      )
    },
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

export function useCreateSiskamling(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiFetch('/api/ketertiban/siskamling', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ketertiban', 'siskamling'] }),
  })
}

export function useUpdateSiskamling(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiFetch(`/api/ketertiban/siskamling/${id}`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ketertiban', 'siskamling'] }),
  })
}

export function useDeleteSiskamling(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/ketertiban/siskamling/${id}`, { method: 'DELETE', desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ketertiban', 'siskamling'] }),
  })
}

export function useBencana(desaId?: string | null, page = 1, jenis?: string, status?: string) {
  const params = new URLSearchParams()
  params.set('page', String(page))
  if (jenis) params.set('jenis', jenis)
  if (status) params.set('status', status)
  return useQuery({
    queryKey: ['bencana', desaId, page, jenis, status],
    queryFn: () =>
      apiFetch<{ data: import('../types').KejadianBencanaItem[]; total: number }>(`/api/bencana?${params}`, { desaId }),
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

export function useCreateBencana(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiFetch('/api/bencana', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bencana'] }),
  })
}

export function useUpdateBencana(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiFetch(`/api/bencana/${id}`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bencana'] }),
  })
}

export function useDeleteBencana(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/bencana/${id}`, { method: 'DELETE', desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bencana'] }),
  })
}

export function useUploadBencanaFoto(desaId?: string | null) {
  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData()
      fd.append('foto', file)
      const headers: Record<string, string> = {}
      if (desaId) headers['X-Desa-Id'] = desaId
      const res = await fetch('/api/bencana/upload-foto', {
        method: 'POST',
        credentials: 'include',
        headers,
        body: fd,
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Gagal upload foto' }))
        throw new Error(err.message || `HTTP ${res.status}`)
      }
      return res.json() as Promise<{ foto_url: string; preview_url: string }>
    },
  })
}

export function useBencanaFotoUrl(desaId?: string | null, bencanaId?: string | null, enabled = false) {
  return useQuery({
    queryKey: ['bencana', 'foto-url', desaId, bencanaId],
    queryFn: () => apiFetch<{ url: string | null }>(`/api/bencana/${bencanaId}/foto-url`, { desaId }),
    enabled: !!desaId && !!bencanaId && enabled,
    staleTime: 55 * 60 * 1000,
  })
}

// ============================================================
// Kelembagaan — Notulen Rapat
// ============================================================

export function useNotulRapat(desaId?: string | null, search?: string, page = 1, year?: number, month?: number) {
  const params = new URLSearchParams()
  params.set('page', String(page))
  if (search) params.set('search', search)
  if (year) params.set('year', String(year))
  if (month) params.set('month', String(month))
  return useQuery({
    queryKey: ['kelembagaan', 'rapat', desaId, search, page, year, month],
    queryFn: () => apiFetch<{ data: any[]; total: number }>(`/api/kelembagaan/rapat?${params}`, { desaId }),
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

export function useCreateRapat(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiFetch('/api/kelembagaan/rapat', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kelembagaan', 'rapat'] }),
  })
}

export function useUpdateRapat(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiFetch(`/api/kelembagaan/rapat/${id}`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kelembagaan', 'rapat'] }),
  })
}

export function useDeleteRapat(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/kelembagaan/rapat/${id}`, { method: 'DELETE', desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kelembagaan', 'rapat'] }),
  })
}

// ============================================================
// Pemilihan — DPT
// ============================================================

export function useDataPemilih(desaId?: string | null, filters?: { tps?: string; search?: string }, page = 1) {
  const params = new URLSearchParams()
  params.set('page', String(page))
  if (filters?.tps) params.set('tps', filters.tps)
  if (filters?.search) params.set('search', filters.search)
  return useQuery({
    queryKey: ['pemilihan', desaId, filters, page],
    queryFn: () => apiFetch<{ data: any[]; total: number }>(`/api/pemilihan?${params}`, { desaId }),
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

export function useCreatePemilih(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiFetch('/api/pemilihan', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pemilihan'] }),
  })
}

export function useUpdatePemilih(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Record<string, unknown>) =>
      apiFetch(`/api/pemilihan/${id}`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pemilihan'] }),
  })
}

export function useDeletePemilih(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/pemilihan/${id}`, { method: 'DELETE', desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pemilihan'] }),
  })
}

export function useGenerateDPT(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiFetch<{ added: number; total_eligible: number }>('/api/pemilihan/generate-dpt', { method: 'POST', desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pemilihan'] }),
  })
}

export function useUpdateWargaStatus(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      status_penduduk,
      tanggal,
      keterangan,
    }: {
      id: string
      status_penduduk: string
      tanggal?: string
      keterangan?: string
    }) =>
      apiFetch(`/api/warga/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status_penduduk, tanggal, keterangan }),
        desaId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warga'] })
      qc.invalidateQueries({ queryKey: ['warga', 'demografi'] })
    },
  })
}

// ============================================================
// QR Verifikasi Surat
// ============================================================

export function useRevokeSurat(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, alasan }: { id: string; alasan: string }) =>
      apiFetch(`/api/surat/${id}/revoke`, {
        method: 'PATCH',
        body: JSON.stringify({ alasan }),
        desaId,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['surat'] }),
  })
}

export function usePublicVerifySurat(kode?: string | null) {
  return useQuery({
    queryKey: ['verify-surat', kode],
    queryFn: () =>
      apiFetch<{
        valid: boolean
        status: string
        message: string
        alasan?: string
        dicabut_pada?: string
        detail?: {
          jenis: string
          nomor?: string
          nama_warga: string
          desa: string
          kecamatan?: string
          kabupaten?: string
          tanggal_approve?: string
        }
        verified_count?: number
      }>(`/api/public/verify/${kode}`),
    enabled: !!kode,
  })
}

// ============================================================
// Portal Transparansi (publik)
// ============================================================

export function usePublicKeuangan(slug?: string | null, tahun?: number) {
  const params = tahun ? `?tahun=${tahun}` : ''
  return useQuery({
    queryKey: ['public-keuangan', slug, tahun],
    queryFn: () => apiFetch<any>(`/api/public/desa/${slug}/keuangan${params}`),
    enabled: !!slug,
  })
}

export function usePublicPembangunan(slug?: string | null, tahun?: number) {
  const params = tahun ? `?tahun=${tahun}` : ''
  return useQuery({
    queryKey: ['public-pembangunan', slug, tahun],
    queryFn: () => apiFetch<any>(`/api/public/desa/${slug}/pembangunan${params}`),
    enabled: !!slug,
  })
}

export function usePublicInfo(slug?: string | null) {
  return useQuery({
    queryKey: ['public-info', slug],
    queryFn: () => apiFetch<any[]>(`/api/public/desa/${slug}/info`),
    enabled: !!slug,
  })
}

// ============================================================
// Booking Layanan
// ============================================================

export function useBookingSlots(desaId?: string | null, tanggal?: string) {
  return useQuery({
    queryKey: ['booking-slots', desaId, tanggal],
    queryFn: () => apiFetch<any>(`/api/booking/slots?tanggal=${tanggal}`, { desaId }),
    enabled: !!desaId && !!tanggal,
  })
}

export function useBookingList(desaId?: string | null, filters?: { tanggal?: string; status?: string }, page = 1) {
  const params = new URLSearchParams()
  params.set('page', String(page))
  if (filters?.tanggal) params.set('tanggal', filters.tanggal)
  if (filters?.status) params.set('status', filters.status)
  return useQuery({
    queryKey: ['booking', desaId, filters, page],
    queryFn: () => apiFetch<{ data: any[]; total: number }>(`/api/booking?${params}`, { desaId }),
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

export function useBookingHariIni(desaId?: string | null, tanggal?: string) {
  return useQuery({
    queryKey: ['booking-hari-ini', desaId, tanggal],
    queryFn: () => apiFetch<any[]>(`/api/booking/hari-ini${tanggal ? `?tanggal=${tanggal}` : ''}`, { desaId }),
    enabled: !!desaId,
    staleTime: 0,
  })
}

export function useCreateBooking(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiFetch('/api/booking', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['booking'] })
      qc.invalidateQueries({ queryKey: ['booking-slots'] })
      qc.invalidateQueries({ queryKey: ['booking-hari-ini'] })
    },
  })
}

export function useUpdateBooking(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; status: string; catatan?: string }) =>
      apiFetch(`/api/booking/${id}`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['booking'] })
      qc.invalidateQueries({ queryKey: ['booking-hari-ini'] })
    },
  })
}

export function usePublicBookingSlots(slug?: string | null, tanggal?: string) {
  return useQuery({
    queryKey: ['public-booking-slots', slug, tanggal],
    queryFn: () => apiFetch<any>(`/api/public/desa/${slug}/booking/slots?tanggal=${tanggal}`),
    enabled: !!slug && !!tanggal,
  })
}

export function usePublicCreateBooking(slug?: string | null) {
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiFetch(`/api/public/desa/${slug}/booking`, { method: 'POST', body: JSON.stringify(data) }),
  })
}

// ============================================================
// e-Musrenbang
// ============================================================

export function useMusrenbangList(
  desaId?: string | null,
  filters?: { status?: string; kategori?: string; search?: string; sort?: string },
  page = 1,
) {
  const params = new URLSearchParams()
  params.set('page', String(page))
  if (filters?.status) params.set('status', filters.status)
  if (filters?.kategori) params.set('kategori', filters.kategori)
  if (filters?.search) params.set('search', filters.search)
  if (filters?.sort) params.set('sort', filters.sort)
  return useQuery({
    queryKey: ['musrenbang', desaId, filters, page],
    queryFn: () => apiFetch<{ data: any[]; total: number }>(`/api/musrenbang?${params}`, { desaId }),
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

export function useMusrenbangStats(desaId?: string | null) {
  return useQuery({
    queryKey: ['musrenbang-stats', desaId],
    queryFn: () => apiFetch<any>('/api/musrenbang/stats', { desaId }),
    enabled: !!desaId,
  })
}

export function useCreateMusrenbang(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiFetch('/api/musrenbang', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['musrenbang'] })
      qc.invalidateQueries({ queryKey: ['musrenbang-stats'] })
    },
  })
}

export function useUpdateMusrenbang(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string
      status?: string
      tahun_anggaran?: number
      alasan?: string
      catatan_desa?: string
    }) => apiFetch(`/api/musrenbang/${id}`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['musrenbang'] })
      qc.invalidateQueries({ queryKey: ['musrenbang-stats'] })
    },
  })
}

export function useDukungMusrenbang(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/musrenbang/${id}/dukung`, { method: 'POST', desaId, body: JSON.stringify({}) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['musrenbang'] })
      qc.invalidateQueries({ queryKey: ['musrenbang-stats'] })
    },
  })
}

export function useRealisasiMusrenbang(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/musrenbang/${id}/realisasi`, { method: 'POST', desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['musrenbang'] })
      qc.invalidateQueries({ queryKey: ['musrenbang-stats'] })
      qc.invalidateQueries({ queryKey: ['proyek'] })
    },
  })
}

export function useEditMusrenbang(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string
      judul?: string
      deskripsi?: string
      kategori?: string
      lokasi?: string
      urgensi?: string
    }) => apiFetch(`/api/musrenbang/${id}/konten`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['musrenbang'] })
    },
  })
}

export function useDeleteMusrenbang(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/musrenbang/${id}`, { method: 'DELETE', desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['musrenbang'] })
      qc.invalidateQueries({ queryKey: ['musrenbang-stats'] })
    },
  })
}

export function usePublicMusrenbang(slug?: string | null) {
  return useQuery({
    queryKey: ['public-musrenbang', slug],
    queryFn: () => apiFetch<any[]>(`/api/public/desa/${slug}/musrenbang`),
    enabled: !!slug,
  })
}

// ============================================================
// Life Events
// ============================================================

export function useLifeEvents(
  desaId?: string | null,
  filters?: { warga_id?: string; event_kode?: string; status?: string },
  page = 1,
) {
  const params = new URLSearchParams()
  params.set('page', String(page))
  if (filters?.warga_id) params.set('warga_id', filters.warga_id)
  if (filters?.event_kode) params.set('event_kode', filters.event_kode)
  if (filters?.status) params.set('status', filters.status)
  return useQuery({
    queryKey: ['life-events', desaId, filters, page],
    queryFn: () => apiFetch<{ data: any[]; total: number }>(`/api/life-events?${params}`, { desaId }),
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

export function useLifeEventStats(desaId?: string | null) {
  return useQuery({
    queryKey: ['life-event-stats', desaId],
    queryFn: () => apiFetch<any>('/api/life-events/stats', { desaId }),
    enabled: !!desaId,
  })
}

export function useLifeEventDefinitions() {
  return useQuery({
    queryKey: ['life-event-definitions'],
    queryFn: () => apiFetch<any[]>('/api/life-events/definitions'),
    staleTime: 1000 * 60 * 60, // 1 hour
  })
}

export function useCreateLifeEvent(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiFetch('/api/life-events', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['life-events'] })
      qc.invalidateQueries({ queryKey: ['life-event-stats'] })
    },
  })
}

export function useCancelLifeEvent(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/life-events/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'dibatalkan' }),
        desaId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['life-events'] })
      qc.invalidateQueries({ queryKey: ['life-event-stats'] })
    },
  })
}

export function useUpdateChecklist(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, item_kode, selesai }: { id: string; item_kode: string; selesai: boolean }) =>
      apiFetch(`/api/life-events/${id}/checklist`, {
        method: 'PATCH',
        body: JSON.stringify({ item_kode, selesai }),
        desaId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['life-events'] })
      qc.invalidateQueries({ queryKey: ['life-event-stats'] })
    },
  })
}

// ─── Portal Warga ──────────────────────────────────────

export function useWargaMe(enabled = true) {
  return useQuery({
    queryKey: ['warga', 'me'],
    queryFn: () =>
      apiFetch<{
        id: string
        nik: string
        nama: string
        no_hp: string | null
        tanggal_lahir: string | null
        tempat_lahir: string | null
        jenis_kelamin: string | null
        agama: string | null
        pekerjaan: string | null
        pendidikan: string | null
        status_kawin: string | null
        alamat: string | null
        desa: {
          id: string
          slug: string
          nama: string
          nama_ai: string | null
          kecamatan: string | null
          kabupaten: string | null
        }
      }>('/api/warga-portal/me'),
    retry: false,
    enabled,
  })
}

export function useWargaSurat() {
  return useQuery({
    queryKey: ['warga', 'surat'],
    queryFn: () =>
      apiFetch<
        Array<{
          id: string
          jenis: string
          status: string
          nomor_surat: string | null
          kode_verifikasi: string | null
          keperluan: string | null
          pdf_url: string | null
          is_revoked: boolean
          catatan_kelengkapan: string | null // GAP-4: apa yang perlu dilengkapi saat status perlu_dilengkapi
          created_at: string
        }>
      >('/api/warga-portal/surat'),
    retry: false,
  })
}

// BUG-G4-7/G4-6: mint link FormWizard on-demand untuk lengkapi surat perlu_dilengkapi.
export function useGenerateLengkapiLink() {
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ url: string }>(`/api/warga-portal/surat/${id}/lengkapi-link`, { method: 'POST' }),
  })
}

export function useWargaLaporan() {
  return useQuery({
    queryKey: ['warga', 'laporan'],
    queryFn: () =>
      apiFetch<
        Array<{
          id: string
          kategori: string
          sub_kategori: string | null
          deskripsi: string
          status: string
          priority: string
          foto_urls: string[]
          lokasi: string | null
          created_at: string
        }>
      >('/api/warga-portal/laporan'),
    retry: false,
  })
}

export function useWargaBooking() {
  return useQuery({
    queryKey: ['warga', 'booking'],
    queryFn: () =>
      apiFetch<
        Array<{
          id: string
          layanan: string
          tanggal: string
          slot: string
          antrian_no: string | null
          status: string
          keterangan: string | null
          catatan: string | null
          created_at: string
        }>
      >('/api/warga-portal/booking'),
    retry: false,
  })
}

export function useWargaLifeEvents() {
  return useQuery({
    queryKey: ['warga', 'life-events'],
    queryFn: () =>
      apiFetch<
        Array<{
          id: string
          event_kode: string
          event_data: any
          checklist: any[]
          status: string
          tanggal_event: string
          created_at: string
        }>
      >('/api/warga-portal/life-events'),
    retry: false,
  })
}

export function useWargaUpdateProfil() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { alamat?: string; no_hp?: string; pekerjaan?: string; pendidikan?: string }) =>
      apiFetch('/api/warga-portal/profil', {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['warga', 'me'] })
    },
  })
}

export function useWargaLogout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiFetch('/api/warga-portal/logout', { method: 'POST' }),
    onSuccess: () => {
      qc.clear()
    },
  })
}

// ── Custom Tools ──

export function useCustomTools(
  desaId?: string | null,
  filters?: { category?: string; enabled?: string; search?: string },
) {
  const params = new URLSearchParams()
  if (filters?.category) params.set('category', filters.category)
  if (filters?.enabled) params.set('enabled', filters.enabled)
  if (filters?.search) params.set('search', filters.search)
  const qs = params.toString()
  return useQuery({
    queryKey: ['custom-tools', desaId, filters?.category, filters?.enabled, filters?.search],
    queryFn: () =>
      apiFetch<{ tools: CustomToolItem[]; total: number }>(`/api/custom-tools${qs ? `?${qs}` : ''}`, { desaId }),
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

export function useCustomTool(desaId: string | null, toolId: string | null) {
  return useQuery({
    queryKey: ['custom-tools', desaId, toolId],
    queryFn: () => apiFetch<CustomToolItem>(`/api/custom-tools/${toolId}`, { desaId }),
    enabled: !!desaId && !!toolId,
  })
}

export function useCreateCustomTool(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiFetch('/api/custom-tools', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['custom-tools', desaId] })
    },
  })
}

export function useUpdateCustomTool(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      apiFetch(`/api/custom-tools/${id}`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['custom-tools', desaId] })
    },
  })
}

export function useDeleteCustomTool(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/custom-tools/${id}`, { method: 'DELETE', desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['custom-tools', desaId] })
    },
  })
}

export function useToggleCustomTool(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      apiFetch(`/api/custom-tools/${id}/toggle`, { method: 'PATCH', body: JSON.stringify({ enabled }), desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['custom-tools', desaId] })
    },
  })
}

export function useTestCustomTool(desaId?: string | null) {
  return useMutation({
    mutationFn: ({ id, params }: { id: string; params?: Record<string, unknown> }) =>
      apiFetch<{
        ok: boolean
        duration_ms: number
        status_code: number
        raw_response: string | null
        extracted_data: unknown
        error?: string
      }>(`/api/custom-tools/${id}/test`, { method: 'POST', body: JSON.stringify({ params }), desaId }),
  })
}

export function useDuplicateCustomTool(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, name, label }: { id: string; name: string; label?: string }) =>
      apiFetch(`/api/custom-tools/${id}/duplicate`, { method: 'POST', body: JSON.stringify({ name, label }), desaId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['custom-tools', desaId] })
    },
  })
}

export function useTestToolChat(desaId?: string | null) {
  return useMutation({
    mutationFn: (data: { question: string; history?: Array<{ role: string; content: string }> }) =>
      apiFetch<{ answer: string; toolsUsed: { name: string; success: boolean; duration: number }[] }>(
        '/api/custom-tools/test-chat',
        { method: 'POST', body: JSON.stringify(data), desaId },
      ),
  })
}

export function useImportSpec(desaId?: string | null) {
  return useMutation({
    mutationFn: (data: { spec: string; base_url?: string; name_prefix?: string; category?: string }) =>
      apiFetch<{
        info: { title: string; version: string; description?: string }
        endpoints: { id: string; method: string; path: string; summary: string; tags: string[] }[]
        drafts: Record<string, unknown>[]
        auth_template: { type: string; fields: { key: string; label: string; placeholder: string }[] } | null
      }>('/api/custom-tools/import-spec', { method: 'POST', body: JSON.stringify(data), desaId }),
  })
}

export function useToolCategories(desaId?: string | null) {
  return useQuery({
    queryKey: ['tool-categories', desaId],
    queryFn: () =>
      apiFetch<{ categories: { name: string; icon: string }[] }>('/api/custom-tools/categories', { desaId }),
    enabled: !!desaId,
  })
}

export function useUpdateToolCategories(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (categories: { name: string; icon: string }[]) =>
      apiFetch<{ success: boolean; categories: { name: string; icon: string }[] }>('/api/custom-tools/categories', {
        method: 'PUT',
        body: JSON.stringify({ categories }),
        desaId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tool-categories', desaId] })
    },
  })
}

export function useImportCreate(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { tools: Record<string, unknown>[]; auth_config?: Record<string, unknown> }) =>
      apiFetch<{ created: number; tools: { id: string; name: string }[]; skipped: string[] }>(
        '/api/custom-tools/import-create',
        {
          method: 'POST',
          body: JSON.stringify(data),
          desaId,
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['custom-tools', desaId] })
    },
  })
}

// ─── Tiket ─────────────────────────

export function useTicketList(filters?: {
  status?: string
  prioritas?: string
  kategori?: string
  search?: string
  page?: number
  from?: string
  to?: string
  sort?: string
  dir?: string
}) {
  const params = new URLSearchParams()
  if (filters?.status) params.set('status', filters.status)
  if (filters?.prioritas) params.set('prioritas', filters.prioritas)
  if (filters?.kategori) params.set('kategori', filters.kategori)
  if (filters?.search) params.set('search', filters.search)
  if (filters?.page) params.set('page', String(filters.page))
  if (filters?.from) params.set('from', filters.from)
  if (filters?.to) params.set('to', filters.to)
  if (filters?.sort) params.set('sort', filters.sort)
  if (filters?.dir) params.set('dir', filters.dir)
  return useQuery({
    queryKey: ['tickets', filters],
    queryFn: () => apiFetch<{ data: any[]; total: number; page: number; limit: number }>(`/api/tickets?${params}`),
  })
}

export function exportTicketsCsv(filters?: {
  status?: string
  prioritas?: string
  kategori?: string
  search?: string
  from?: string
  to?: string
  sort?: string
  dir?: string
}) {
  const params = new URLSearchParams()
  if (filters?.status) params.set('status', filters.status)
  if (filters?.prioritas) params.set('prioritas', filters.prioritas)
  if (filters?.kategori) params.set('kategori', filters.kategori)
  if (filters?.search) params.set('search', filters.search)
  if (filters?.from) params.set('from', filters.from)
  if (filters?.to) params.set('to', filters.to)
  if (filters?.sort) params.set('sort', filters.sort)
  if (filters?.dir) params.set('dir', filters.dir)
  window.open(`/api/tickets/export/csv?${params}`, '_blank')
}

export function useTicketDetail(id?: string | null) {
  return useQuery({
    queryKey: ['ticket', id],
    queryFn: () => apiFetch<any>(`/api/tickets/${id}`),
    enabled: !!id,
  })
}

export function useTicketSummary(enabled = true) {
  return useQuery({
    queryKey: ['tickets', 'summary'],
    queryFn: () => apiFetch<{ open: number; in_progress: number; total_active: number }>('/api/tickets/summary/counts'),
    refetchInterval: 30_000,
    enabled,
  })
}

export function useCreateTicket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      judul: string
      deskripsi: string
      kategori?: string
      prioritas?: string
      url?: string
      trace_id?: string
      lampiran?: any[]
      desa_id?: string
    }) => apiFetch('/api/tickets', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets'] })
    },
  })
}

export function useUpdateTicket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; [key: string]: any }) =>
      apiFetch(`/api/tickets/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: (_data: any, vars: any) => {
      qc.invalidateQueries({ queryKey: ['tickets'] })
      qc.invalidateQueries({ queryKey: ['ticket', vars.id] })
    },
  })
}

export function useDeleteTicket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch(`/api/tickets/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets'] })
    },
  })
}

export function useBulkDeleteTickets() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (ids: string[]) =>
      apiFetch<{ ok: boolean; deleted: number }>('/api/tickets/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ ids }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tickets'] })
    },
  })
}

export function useAddTicketComment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ticketId, isi, lampiran }: { ticketId: string; isi: string; lampiran?: any[] }) =>
      apiFetch(`/api/tickets/${ticketId}/comments`, {
        method: 'POST',
        body: JSON.stringify({ isi, lampiran }),
      }),
    onSuccess: (_data: any, vars: any) => {
      qc.invalidateQueries({ queryKey: ['ticket', vars.ticketId] })
    },
  })
}

export function useUploadTicketAttachment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ ticketId, file }: { ticketId: string; file: File }) => {
      const formData = new FormData()
      formData.append('file', file)
      const res = await fetch(`/api/tickets/${ticketId}/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      })
      if (!res.ok) throw new Error('Upload gagal')
      return res.json()
    },
    onSuccess: (_data: any, vars: any) => {
      qc.invalidateQueries({ queryKey: ['ticket', vars.ticketId] })
      qc.invalidateQueries({ queryKey: ['ticket-attachments', vars.ticketId] })
    },
  })
}

export function useTicketAttachments(ticketId?: string | null) {
  return useQuery({
    queryKey: ['ticket-attachments', ticketId],
    queryFn: () =>
      apiFetch<{ attachments: Array<{ objectName: string; url: string | null }> }>(
        `/api/tickets/${ticketId}/attachments`,
      ),
    enabled: !!ticketId,
  })
}

// ─── Maintenance ──────────────────────────────────────

interface MaintenanceStatus {
  active: boolean
  until: string | null
  message: string | null
}

export function useMaintenanceStatus() {
  return useQuery({
    queryKey: ['maintenance-status'],
    queryFn: () => apiFetch<MaintenanceStatus>('/api/maintenance/status'),
    refetchInterval: 10_000,
    staleTime: 5_000,
    refetchOnWindowFocus: true,
  })
}

export function useToggleMaintenance() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { active: boolean; until?: string | null; message?: string | null }) =>
      apiFetch<MaintenanceStatus>('/api/maintenance/toggle', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['maintenance-status'] }),
  })
}

// ─── AI Provider Config ───────────────────────────────

export type AiProviderName = 'openrouter' | 'custom'

export interface AiTierConfig {
  provider: AiProviderName
  model: string
}

export interface AiTiersConfig {
  micro: AiTierConfig
  fast: AiTierConfig
  smart: AiTierConfig
}

export interface AiCredentialEntry {
  api_key?: string
  base_url?: string
  auth?: string
  name?: string
  source?: 'db' | 'not_configured'
}

export interface AiCredentialsResponse {
  openrouter: AiCredentialEntry
  custom: AiCredentialEntry
  jina: AiCredentialEntry
  ollama_embed: AiCredentialEntry
}

export type EmbedProviderName = 'jina' | 'openrouter' | 'ollama'

export function useAiTiers() {
  return useQuery({
    queryKey: ['admin', 'ai-routing'],
    queryFn: () => apiFetch<AiTiersConfig>('/api/admin/ai-routing'),
  })
}

export function useUpdateAiTiers() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: AiTiersConfig) =>
      apiFetch<AiTiersConfig>('/api/admin/ai-routing', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'ai-routing'] }),
  })
}

export function useAiCredentials() {
  return useQuery({
    queryKey: ['admin', 'ai-credentials'],
    queryFn: () => apiFetch<AiCredentialsResponse>('/api/admin/ai-credentials'),
  })
}

export function useUpdateAiCredentials() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      openrouter?: { api_key?: string }
      custom?: { name?: string; base_url?: string; api_key?: string }
      jina?: { api_key?: string }
      ollama_embed?: { base_url?: string; api_key?: string }
    }) =>
      apiFetch<{ ok: boolean }>('/api/admin/ai-credentials', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'ai-credentials'] })
      qc.invalidateQueries({ queryKey: ['admin', 'ai-routing'] })
    },
  })
}

export function useEmbedProvider() {
  return useQuery({
    queryKey: ['admin', 'embed-provider'],
    queryFn: () => apiFetch<{ provider: EmbedProviderName }>('/api/admin/embed-provider'),
  })
}

export function useUpdateEmbedProvider() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { provider: EmbedProviderName }) =>
      apiFetch<{ ok: boolean; provider: EmbedProviderName }>('/api/admin/embed-provider', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'embed-provider'] }),
  })
}

export function useTestEmbedProvider() {
  return useMutation({
    mutationFn: (data: { provider: EmbedProviderName }) =>
      apiFetch<{
        ok: boolean
        provider: EmbedProviderName
        dimensions?: number
        latency_ms: number
        error?: string
      }>('/api/admin/embed-credentials/test', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  })
}

export function useReindexEmbeddings() {
  return useMutation({
    mutationFn: (data: { desa_id?: string } = {}) =>
      apiFetch<{ reindexed: number; errors: number; desa_count: number }>('/api/admin/reindex-embeddings', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  })
}

export function useAiProviderModels(provider?: AiProviderName | null) {
  return useQuery({
    queryKey: ['admin', 'ai-models', provider],
    queryFn: () =>
      apiFetch<{ models: Array<{ id: string; name?: string }>; error?: string }>(
        `/api/admin/ai-routing/models?provider=${provider}`,
      ),
    enabled: !!provider,
    staleTime: 3_600_000,
  })
}

export function useTestAiProvider() {
  return useMutation({
    mutationFn: (data: { provider: AiProviderName; model: string }) =>
      apiFetch<{ ok: boolean; latency_ms: number; response?: string; error?: string }>('/api/admin/ai-routing/test', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  })
}

// ─── WA Repair Budget (panel dev super-admin) ───
export interface WaRepairBudgetItem {
  desaId: string
  slug: string
  nama: string
  instance: string
  blocked: boolean
  remainingMs: number
  remainingLabel: string | null
  lastPairedAt: number | null
}

/** Status blokir circuit-breaker re-pair per instance (dibaca dari KV, nol call WA). */
export function useWaRepairBudget() {
  return useQuery({
    queryKey: ['dev', 'wa-repair-budget'],
    queryFn: () => apiFetch<{ items: WaRepairBudgetItem[] }>('/api/dev/wa-repair-budget'),
  })
}

/** Buka blokir re-pair satu instance (super-admin). Invalidate list setelah sukses. */
export function useResetWaRepairBudget() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (instance: string) =>
      apiFetch<{ ok: boolean; instance: string }>('/api/dev/wa-repair-budget/reset', {
        method: 'POST',
        body: JSON.stringify({ instance }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dev', 'wa-repair-budget'] })
    },
  })
}

// ─── GAP-1: Pengantar Wilayah (pohon teritorial intra-desa + pejabat) ───
// Namespace query 'pengantar-wilayah' sengaja beda dari 'wilayah' nasional (idn-area-data).
export interface WilayahNode {
  id: string
  desa_id: string
  parent_id: string | null
  level: number
  level_label: string
  nama: string
  kode: string | null
  urutan: number
  aktif: boolean
  created_at: string
  updated_at: string
  _count: { children: number; warga: number; pejabat: number }
}

export interface WilayahPejabatItem {
  id: string
  desa_id: string
  wilayah_id: string
  user_id: string | null
  warga_id: string | null
  nama: string
  no_hp: string | null
  jabatan_label: string
  is_pemberi_pengantar: boolean
  aktif: boolean
  ttd_url: string | null
  sk_url: string | null
  mulai_menjabat: string | null
  selesai_menjabat: string | null
  wilayah: { id: string; nama: string; level_label: string }
}

export function useWilayahTree(desaId?: string | null) {
  return useQuery({
    queryKey: ['pengantar-wilayah', desaId],
    queryFn: () => apiFetch<WilayahNode[]>('/api/pengantar-wilayah', { desaId }),
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

export interface WilayahInput {
  parent_id?: string | null
  level: number
  level_label: string
  nama: string
  kode?: string | null
  urutan?: number
  aktif?: boolean
}

export function useCreateWilayah(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: WilayahInput) =>
      apiFetch<WilayahNode>('/api/pengantar-wilayah', { method: 'POST', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pengantar-wilayah', desaId] }),
  })
}

export function useUpdateWilayah(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<WilayahInput> }) =>
      apiFetch<WilayahNode>(`/api/pengantar-wilayah/${id}`, { method: 'PATCH', body: JSON.stringify(data), desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pengantar-wilayah', desaId] }),
  })
}

export function useDeleteWilayah(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiFetch<{ ok: true }>(`/api/pengantar-wilayah/${id}`, { method: 'DELETE', desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pengantar-wilayah', desaId] }),
  })
}

export function useWilayahPejabat(desaId?: string | null, wilayahId?: string | null) {
  return useQuery({
    queryKey: ['pengantar-wilayah-pejabat', desaId, wilayahId ?? null],
    queryFn: () =>
      apiFetch<WilayahPejabatItem[]>(`/api/pengantar-wilayah/pejabat${wilayahId ? `?wilayah_id=${wilayahId}` : ''}`, {
        desaId,
      }),
    enabled: !!desaId,
    placeholderData: keepPreviousData,
  })
}

export interface PejabatInput {
  wilayah_id: string
  user_id?: string | null
  warga_id?: string | null
  nama: string
  no_hp?: string | null
  jabatan_label: string
  is_pemberi_pengantar?: boolean
  mulai_menjabat?: string | null
}

export function useTunjukPejabat(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: PejabatInput) =>
      apiFetch<WilayahPejabatItem>('/api/pengantar-wilayah/pejabat', {
        method: 'POST',
        body: JSON.stringify(data),
        desaId,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pengantar-wilayah-pejabat', desaId] }),
  })
}

export function useUpdatePejabat(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Omit<PejabatInput, 'wilayah_id'>> }) =>
      apiFetch<WilayahPejabatItem>(`/api/pengantar-wilayah/pejabat/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
        desaId,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pengantar-wilayah-pejabat', desaId] }),
  })
}

export function useNonaktifPejabat(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<WilayahPejabatItem>(`/api/pengantar-wilayah/pejabat/${id}/nonaktif`, { method: 'POST', desaId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pengantar-wilayah-pejabat', desaId] }),
  })
}

/** Upload aset pejabat (kind='ttd' gambar wajib | 'sk' gambar/pdf) ke MinIO via multipart. */
export function useUploadPejabatAsset(desaId?: string | null) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, kind, file }: { id: string; kind: 'ttd' | 'sk'; file: File }) => {
      const formData = new FormData()
      formData.append('file', file)
      const headers: Record<string, string> = {}
      if (desaId) headers['X-Desa-Id'] = desaId
      const res = await fetch(`/api/pengantar-wilayah/pejabat/${id}/asset/${kind}`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: formData,
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: 'Upload gagal' }))
        throw new Error(err.message || `HTTP ${res.status}`)
      }
      return res.json() as Promise<{ ok: boolean; url: string }>
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pengantar-wilayah-pejabat', desaId] }),
  })
}
